import { Flex, Alert, Button, Modal, Tooltip, notification, Spin } from "antd";
import { FileText, PlusCircle, Sparkles } from "lucide-react";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import { useAtom } from "jotai";
import { canvasBackgrounds } from "./backgrounds/canvasBackgrounds";
import CanvasBackground from "./components/CanvasBackground";
import CanvasStage from "./components/CanvasStage";
import Countdown from "./components/Countdown";
import DraggableResizableStream from "./components/DraggableResizableStream";
import { wrapperWidthAtom, wrapperHeightAtom, isRatioTransitioningAtom, canvasTransitionSnapshotAtom } from "./store/canvasStore";
import { store } from "./store/canvasStoreInstance";
import MobileNotAllowedOverlay from "./components/MobileNotAllowedOverlay";
import NoStreamLayer from "./components/NoStreamLayer";
import RecordingToolbarBottom, { RecordingToolbarHandle } from "./components/RecordingToolbarBottom";
import SelectionToolbar from "./components/SelectionToolbar";
import useLayoutManager from "./context/useLayoutManager";
import useRecorderContext from "./context/useRecorderContext";
import { useBeforeUnloadWarning } from "./hooks/useBeforeUnloadWarning";
import useCanvasBackground from "./hooks/useCanvasBackground";
import { useCanvasStreamDetection } from "./hooks/useCanvasStreamDetection";
import { useMobileDetection } from "./hooks/useMobileDetection";
import useStreamVideo from "./hooks/useStreamVideo";
import { deleteUpload, db, getPendingRecordingBySceneId, deletePendingRecording } from "./idb/chunk-db";
import { downloadChunkedBlob } from "./utils/downloadChunkedBlob";
import {
  getCompatibilityWarnings,
  isRecordingSupported,
} from "./utils/browserCompatibility";

import "./CanvasRecorder.scss";
import SavingVideoOverlay from "./components/SavingVideoOverlay";
import StuckChunksOverlay from "./components/StuckChunksOverlay";
import DownloadOnlyOverlay from "./components/DownloadOnlyOverlay";
import UploadCompleteNotification from "./components/UploadCompleteNotification";

import MutePopupOverlay from "./components/MutePopupOverlay";
import { BlankRecordingRecoveryBanner } from "./components/BlankRecordingRecoveryBanner";
import { useBlankRecordingRecovery } from "./hooks/useBlankRecordingRecovery";

import logger from "./utils/logger";
import { resSize } from "./utils/resSize";
import useOutsideClick from "./hooks/useOutsideClick";
import { useSceneSync } from "./hooks/useSceneSync";
import { useProjectLoader } from "./hooks/useProjectLoader";
import { canAddScene, PROJECT_LIMITS } from "./config/projectLimits";

// Sprint 10: Real-time monitoring components
import { LiveUploadMonitor } from "./components/LiveUploadMonitor";
import { UploadQueuePanel } from "./components/UploadQueuePanel";
import { useLiveUploadMonitor } from "./hooks/useLiveUploadMonitor";

// Sprint 14: Template system
import { TemplateGallery } from "./components/templates";
import { useFeatureFlag } from "./services/featureFlags";
import { loadedTemplateAtom } from "./atoms/templateAtoms";

// Sprint 15-16: Scene Timeline Editor & Stitching
import {
  RecordingPreviewModal,
  SceneStitchingEngine,
  AddSceneModal,
} from "./components/scenes";
import TransitionStudioModal from "./components/scenes/TransitionStudioModal";
import { useAtom as useJotaiAtom } from "jotai";
import {
  sceneListAtom,
  activeProjectAtom,
  loadProjectIntoStateAtom,
  currentSceneAtom,
  currentSceneIndexAtom,
  isTeleprompterOpenAtom,
  recordingPreviewAtom,
  showRecordingPreviewAtom,
  isStitchingModalVisibleAtom,
  addSceneModalVisibleAtom,
  layoutModeAtom,
  selectedSceneIdAtom,
  isProjectLoadingAtom,
  isBlankModeAtom,
  resetSceneStateAtom,
} from "./atoms/sceneAtoms";
import { SlidesLayout } from "./components/layouts";
import { Scene } from "../../types/scene";
import SceneService from "./services/sceneService";
import { projectsManager } from "./services/projectsManager";
import { getDefaultTransition } from "./constants/transitionConstants";

// Sprint 17: AI Integration
import { CoachingOverlay } from "./components/ai";

// Sprint 18: Platform Export
import {
  PlatformExportSelector,
  ExportProgressModal,
} from "./components/export";

// Slides Layout Modals
import { CopySettingsModal } from "./components/modals/CopySettingsModal";
import { ProjectCreationOverlay } from "./components/ProjectsDashboard/ProjectCreationOverlay";
import { EditSettingsModal } from "./components/modals/EditSettingsModal";
import { ScenePreviewModal } from "./components/modals/ScenePreviewModal";

// Phase 3: UX & Browser Compatibility
import { useBrowserDetection } from "./hooks/useBrowserDetection";
import { BrowserNotAllowedOverlay } from "./components/overlays/BrowserNotAllowedOverlay";

const CanvasRecorder: React.FC = () => {
  const {
    canvasWidth,
    canvasHeight,
    setCanvasWidth,
    setCanvasHeight,
    screenStream,
    webcamStream,
    microphoneStream,
    isManualPreset,
    selectedStream,
    setSelectedStream,
    initialStreamLayout,
    setInitialStreamLayout,
    canvasRatio,
    setCanvasRatio,
    recordingQuality,
    selectedPreset,
    setSelectedPreset,
    showCountdown,
    setShowCountdown,
    canvasReady,
    setCanvasReady,
    roundedWebcam,
    isRecording,
    recordingPending,
    isPaused,
    isUploading,
    isDeleting,
    deleteRecording,
    startRecording,
    hasManualModifications,
    layoutVersion,
    activeUploads,
    uploadInitializing,
    pendingChunks,
    uploadErrors,
    failedChunks,
    showRetryButton,
    retryUpload,
    currentVideoGuid,
    // Stuck chunks handling
    showStuckChunksOverlay,
    stuckChunks,
    totalStuckChunks,
    isProcessingStuckChunks,
    rawMicrophoneStream,
    onRetryStuckChunks,
    onDiscardStuckChunks,
    // Download-only overlay state
    showDownloadOnlyOverlay,
    setShowDownloadOnlyOverlay,
    // Upload notifications
    handleNotificationClose,
    getLatestNotification,
    stageRef,
    // Outside click state
    setClickedOutside,
    clickedOutside,
    // Microphone mute state
    isMicrophoneMuted,
    // Full screen share detection
    isFullScreenShare,
    setIsFullScreenShare,
    // Background controls
    backgroundColor,
    backgroundImage,
    setBackgroundColor,
    setBackgroundImage,
    setIntendedBackgroundImage,
    // Layout reset for aspect ratio changes
    setLayoutVersion,
    setCanvasInfos,
  } = useRecorderContext();

  const [wrapperWidth, setWrapperWidth] = useAtom(wrapperWidthAtom);
  const [wrapperHeight, setWrapperHeight] = useAtom(wrapperHeightAtom);

  const [showMutePopup, setShowMutePopup] = useState(false);

  // Dual-entry workflow: Get mode from URL parameters
  const [searchParams] = useSearchParams();
  const recordingMode = searchParams.get("mode") || "project"; // 'project' or 'blank'
  const isBlankModeFromUrl = recordingMode === "blank";

  // Sprint 14: Template system
  const isTemplatesUIEnabled = useFeatureFlag("templates-ui");
  const [showTemplateGallery, setShowTemplateGallery] = useState(false);
  const [hideTemplateGalleryModal, setHideTemplateGalleryModal] = useState(false);

  // Sprint 17: AI Integration
  const isDeliveryCoachingEnabled = useFeatureFlag("ai-delivery-coaching");

  // Sprint 18: Platform Export
  const isPlatformExportEnabled = useFeatureFlag("platform-export");

  // Phase 3: Browser Detection
  const isBrowserDetectionEnabled = useFeatureFlag("browser-detection-overlay");
  const { isUnsupportedBrowser, unsupportedBrowserType, canRecord } = useBrowserDetection();
  const [browserWarningDismissed, setBrowserWarningDismissed] = useState(false);

  // Sprint 15-16: Scene Timeline
  const isSceneTimelineEnabled = useFeatureFlag("scene-timeline");
  const [sceneList] = useJotaiAtom(sceneListAtom);
  const [activeProject] = useJotaiAtom(activeProjectAtom);
  const [loadedTemplate] = useJotaiAtom(loadedTemplateAtom);
  const [, loadProject] = useJotaiAtom(loadProjectIntoStateAtom);
  
  // Detect blank mode: URL param takes precedence, then project metadata
  // When loading an existing project (mode=project), only use explicit metadata.mode to determine blank mode
  // This prevents legacy recording projects (without templateId or metadata) from being treated as blank
  const projectMetadataMode = (activeProject as any)?.metadata?.mode;
  const isLoadingExistingProject = recordingMode === 'project';
  
  // For loaded projects: only explicit metadata.mode === 'blank' makes it blank mode
  // For new projects: also check templateId == null as fallback for newly created blank projects
  const isBlankModeFromProject = isLoadingExistingProject 
    ? projectMetadataMode === 'blank'
    : (projectMetadataMode === 'blank' || (activeProject && activeProject.templateId == null && projectMetadataMode !== 'recording'));
  const isBlankMode = isBlankModeFromUrl || isBlankModeFromProject;
  
  // Sync blank mode state to atom for use by export atoms
  const [, setIsBlankModeAtom] = useJotaiAtom(isBlankModeAtom);
  useEffect(() => {
    setIsBlankModeAtom(!!isBlankMode);
  }, [isBlankMode, setIsBlankModeAtom]);
  
  const [currentScene, setCurrentScene] = useJotaiAtom(currentSceneAtom);
  const [, resetSceneState] = useJotaiAtom(resetSceneStateAtom);
  const [, showRecordingPreviewForRecovery] = useJotaiAtom(showRecordingPreviewAtom);
  
  const {
    recoverableSessions,
    isRecovering: isRecoveringBlankSession,
    recoveryProgress: blankRecoveryProgress,
    recoverSession: recoverBlankSession,
    discardSession: discardBlankSession,
    discardAll: discardAllBlankSessions,
  } = useBlankRecordingRecovery(isUploading);
  
  // State for recovered pending recording blob URL (set when user clicks "Confirm scene")
  const [recoveredVideoUrl, setRecoveredVideoUrl] = useState<string | null>(null);
  const [recoveredPendingData, setRecoveredPendingData] = useState<any>(null);
  
  // Reset ALL scene state and background when entering blank mode via URL
  // This prevents project backgrounds from persisting when navigating to blank mode
  // Must clear activeProject, currentScene, and sceneList to stop any sync from re-applying old state
  useEffect(() => {
    if (isBlankModeFromUrl) {
      resetSceneState();
      const defaultBg = canvasBackgrounds[0];
      if (defaultBg.type === 'gradient' && defaultBg.color) {
        setBackgroundColor(defaultBg.color);
      } else if (defaultBg.type === 'color' && defaultBg.color) {
        setBackgroundColor(defaultBg.color);
      }
      setBackgroundImage(null);
      localStorage.removeItem("teleprompter_text");
      window.dispatchEvent(
        new CustomEvent("teleprompter-script-update", { detail: { script: "" } }),
      );
    }
  }, [isBlankModeFromUrl, setBackgroundColor, setBackgroundImage, resetSceneState]);
  const [, setCurrentSceneIndex] = useJotaiAtom(currentSceneIndexAtom);
  const [recordingPreview] = useJotaiAtom(recordingPreviewAtom);
  const [isStitchingModalVisible, setIsStitchingModalVisible] = useJotaiAtom(
    isStitchingModalVisibleAtom,
  );
  const [isAddSceneModalVisible, setIsAddSceneModalVisible] = useJotaiAtom(
    addSceneModalVisibleAtom,
  );
  
  // Delete scene confirmation state
  const [isDeleteSceneModalVisible, setIsDeleteSceneModalVisible] = useState(false);
  const [sceneToDelete, setSceneToDelete] = useState<string | null>(null);
  const [isDeletingScene, setIsDeletingScene] = useState(false);
  
  // Scene creation loading state
  const [isCreatingScene, setIsCreatingScene] = useState(false);
  const [sceneCreationMessage, setSceneCreationMessage] = useState('Creating scene...');
  const [layoutMode] = useJotaiAtom(layoutModeAtom);
  const [selectedSceneId, setSelectedSceneId] =
    useJotaiAtom(selectedSceneIdAtom);
  const [isProjectLoading] = useJotaiAtom(isProjectLoadingAtom);

  // Slides Layout Modals State
  const [copySettingsModalVisible, setCopySettingsModalVisible] =
    useState(false);
  const [copySettingsSourceScene, setCopySettingsSourceScene] =
    useState<Scene | null>(null);
  const [editSettingsModalVisible, setEditSettingsModalVisible] =
    useState(false);
  const [editSettingsScene, setEditSettingsScene] = useState<Scene | null>(
    null,
  );
  const [scenePreviewModalVisible, setScenePreviewModalVisible] =
    useState(false);
  const [scenePreviewScene, setScenePreviewScene] = useState<Scene | null>(
    null,
  );
  // Unified transition studio state (replaces separate edit/preview states)
  const [transitionStudioSceneId, setTransitionStudioSceneId] = useState<
    string | null
  >(null);
  const [savingTransitionSceneId, setSavingTransitionSceneId] = useState<
    string | null
  >(null);

  // Sprint 10: Live upload monitoring
  const {
    uploads,
    updateProgress,
    pauseUpload,
    resumeUpload,
    cancelUpload,
    clearCompletedUploads,
  } = useLiveUploadMonitor();

  // Mobile device detection
  const { isMobile, isMobileViewport, deviceType } = useMobileDetection();

  // Canvas-based stream detection
  const { hasAnyCanvasStream } = useCanvasStreamDetection();

  // Browser compatibility state
  const [compatibilityWarnings, setCompatibilityWarnings] = useState<string[]>(
    [],
  );
  const [recordingSupported, setRecordingSupported] = useState(true);

  // Download and discard state
  const [isDownloadingAndDiscarding, setIsDownloadingAndDiscarding] =
    useState(false);
  const [downloadProgress, setDownloadProgress] = useState<{
    current: number;
    total: number;
    currentVideoGuid: string;
  } | null>(null);

  // Scene update loading state
  const [isUpdatingScene, setIsUpdatingScene] = useState(false);

  const { videoRef: screenVideoRef } = useStreamVideo(screenStream, {
    streamType: "screen",
  });
  const { videoRef: webcamVideoRef } = useStreamVideo(webcamStream);

  useProjectLoader();

  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const layerRef = useRef<any>(null);
  const recordingAreaRef = useRef<HTMLDivElement>(null);
  const recordingToolbarRef = useRef<RecordingToolbarHandle>(null);
  // Download and discard all stuck chunks
  const handleDownloadAndDiscard = useCallback(async () => {
    if (isDownloadingAndDiscarding || stuckChunks.length === 0) return;

    setIsDownloadingAndDiscarding(true);
    setDownloadProgress({
      current: 0,
      total: stuckChunks.length,
      currentVideoGuid: "",
    });

    try {
      for (let i = 0; i < stuckChunks.length; i++) {
        const { videoGuid } = stuckChunks[i];

        setDownloadProgress((prev) =>
          prev
            ? {
                ...prev,
                current: i + 1,
                currentVideoGuid: videoGuid,
              }
            : null,
        );

        try {
          // Download the video file
          await downloadChunkedBlob(videoGuid, {
            cleanupAfter: true, // This will clean up chunks after download
          });

          // Also clean up upload records
          await deleteUpload(videoGuid);

          logger.info(
            `Successfully downloaded and cleaned up videoGuid: ${videoGuid}`,
          );
          setShowDownloadOnlyOverlay(false);
        } catch (error) {
          logger.error(`Failed to download videoGuid ${videoGuid}:`, error);
          // Continue with other downloads even if one fails
        }
      }

      // After all downloads are complete, call the discard function to close the overlay
      onDiscardStuckChunks();

      logger.info("Download and discard process completed");
    } catch (error) {
      logger.error("Error during download and discard process:", error);
    } finally {
      setIsDownloadingAndDiscarding(false);
      setDownloadProgress(null);
    }
  }, [isDownloadingAndDiscarding, stuckChunks, onDiscardStuckChunks]);

  // Handler for creating a blank project
  const handleStartBlankProject = useCallback(async () => {
    try {
      const blankProject = await SceneService.createProject({
        title: "Untitled Project",
        scenes: [], // Empty scenes array - users will add scenes manually
        totalDuration: 0,
        status: "draft" as const,
        metadata: { mode: 'blank' }, // Mark as blank mode for limit exemption
      });

      loadProject(blankProject);
      logger.info("[CanvasRecorder] Blank project created:", blankProject.id);
    } catch (error) {
      logger.error("[CanvasRecorder] Failed to create blank project:", error);
    }
  }, [loadProject]);

  useEffect(() => {
    const projectTitle = activeProject?.title;
    if (projectTitle) {
      document.title = `${projectTitle} | Dadan Recording Studio`;
    } else {
      document.title = 'Dadan Recording Studio';
    }
    return () => {
      document.title = 'Dadan Recording Studio';
    };
  }, [activeProject?.title]);

  // Handler for opening the Add Scene modal
  const handleAddScene = useCallback(() => {
    if (!activeProject) {
      logger.error("[CanvasRecorder] Cannot add scene: No active project");
      return;
    }
    
    // Check scene limit for recording mode (not blank mode)
    if (!canAddScene(sceneList.length, isBlankMode)) {
      notification.warning({
        message: 'Scene Limit Reached',
        description: `Maximum ${PROJECT_LIMITS.MAX_SCENES_RECORDING_MODE} scenes allowed per project in recording mode.`,
        placement: 'topRight',
      });
      return;
    }
    
    setIsAddSceneModalVisible(true);
  }, [activeProject, setIsAddSceneModalVisible, sceneList.length, isBlankMode]);

  // Handler for creating a blank scene
  const handleCreateBlankScene = useCallback(async () => {
    if (!activeProject || !activeProject.id) {
      logger.error("[CanvasRecorder] Cannot add scene: No active project or project ID",
        { activeProject, hasId: !!activeProject?.id }
      );
      notification.error({
        message: 'Creation Failed',
        description: 'No active project found. Please reload the page and try again.',
        placement: 'topRight',
      });
      return;
    }
    
    // Check scene limit for recording mode (not blank mode)
    if (!canAddScene(sceneList.length, isBlankMode)) {
      notification.warning({
        message: 'Scene Limit Reached',
        description: `Maximum ${PROJECT_LIMITS.MAX_SCENES_RECORDING_MODE} scenes allowed per project in recording mode.`,
        placement: 'topRight',
      });
      setIsAddSceneModalVisible(false);
      return;
    }

    let sceneCreated = false;
    let newScene: Scene | null = null;

    // Show loading overlay
    setSceneCreationMessage('Creating scene...');
    setIsCreatingScene(true);
    setIsAddSceneModalVisible(false);

    try {
      const sceneNumber = sceneList.length + 1;

      newScene = await SceneService.addSceneToProject(activeProject.id, {
        order: sceneList.length,
        title: `Scene ${sceneNumber}`,
        duration: 0,
        videoChunks: [],
        layout: [],
        background: { type: "solid" as const, colors: ["#1F2937"] },
        script: "",
        status: "pending" as const,
        transitionOut: getDefaultTransition("cut"),
        tags: [],
        notes: "",
      });

      sceneCreated = true;

      // Reload the project to update the scene list (merges with pending_upload scenes)
      const updatedProject = await SceneService.getProjectById(
        activeProject.id,
      );

      if (updatedProject) {
        loadProject(updatedProject);
      }

      logger.info(
        "[CanvasRecorder] Blank scene added to project:",
        newScene.id,
      );
      
      notification.success({
        message: 'Scene Created',
        description: `${newScene.title} has been added to your project.`,
        placement: 'topRight',
      });
    } catch (error) {
      logger.error("[CanvasRecorder] Failed to add blank scene:", error);
      
      notification.error({
        message: sceneCreated ? 'UI Update Failed' : 'Creation Failed',
        description: sceneCreated 
          ? `${newScene?.title} was created but failed to update the UI. Check console for details.`
          : 'Failed to create the scene. Please try again.',
        placement: 'topRight',
      });
    } finally {
      setIsCreatingScene(false);
    }
  }, [activeProject, sceneList.length, loadProject, setIsAddSceneModalVisible, isBlankMode]);

  // Handler for duplicating the previous scene
  const handleDuplicateScene = useCallback(async () => {
    if (!activeProject || !activeProject.id) {
      logger.error(
        "[CanvasRecorder] Cannot duplicate scene: No active project or project ID",
        { activeProject, hasId: !!activeProject?.id }
      );
      notification.error({
        message: 'Duplication Failed',
        description: 'No active project found. Please reload the page and try again.',
        placement: 'topRight',
      });
      return;
    }
    
    // Check scene limit for recording mode (not blank mode)
    if (!canAddScene(sceneList.length, isBlankMode)) {
      notification.warning({
        message: 'Scene Limit Reached',
        description: `Maximum ${PROJECT_LIMITS.MAX_SCENES_RECORDING_MODE} scenes allowed per project in recording mode.`,
        placement: 'topRight',
      });
      setIsAddSceneModalVisible(false);
      return;
    }

    let sceneCreated = false;
    let duplicatedScene: Scene | null = null;

    // Show loading overlay
    setSceneCreationMessage('Duplicating scene...');
    setIsCreatingScene(true);
    setIsAddSceneModalVisible(false);

    try {
      duplicatedScene = await SceneService.duplicatePreviousScene(
        activeProject.id,
      );

      sceneCreated = true;

      // Reload the project to update the scene list (merges with pending_upload scenes)
      const updatedProject = await SceneService.getProjectById(
        activeProject.id,
      );
      if (updatedProject) {
        loadProject(updatedProject);
      }

      logger.info("[CanvasRecorder] Scene duplicated:", duplicatedScene.id);
      
      notification.success({
        message: 'Scene Duplicated',
        description: `${duplicatedScene.title} has been added to your project.`,
        placement: 'topRight',
      });
    } catch (error) {
      logger.error("[CanvasRecorder] Failed to duplicate scene:", error);
      
      notification.error({
        message: sceneCreated ? 'UI Update Failed' : 'Duplication Failed',
        description: sceneCreated 
          ? `${duplicatedScene?.title} was created but failed to update the UI. Check console for details.`
          : 'Failed to duplicate the scene. Please try again.',
        placement: 'topRight',
      });
    } finally {
      setIsCreatingScene(false);
    }
  }, [activeProject, loadProject, setIsAddSceneModalVisible, sceneList.length, isBlankMode]);

  // Slides layout handlers
  const handleRenameScene = useCallback(
    async (sceneId: string) => {
      const scene = sceneList.find((s) => s.id === sceneId);
      if (!scene) return;

      const newTitle = prompt("Enter new scene title:", scene.title);
      if (newTitle && newTitle.trim() && newTitle !== scene.title) {
        try {
          await SceneService.updateScene(sceneId, { title: newTitle.trim() });
          const updatedProject = await SceneService.getProjectById(
            activeProject!.id,
          );
          if (updatedProject) {
            loadProject(updatedProject);
          }
          logger.info("[CanvasRecorder] Scene renamed:", sceneId);
        } catch (error) {
          logger.error("[CanvasRecorder] Failed to rename scene:", error);
        }
      }
    },
    [sceneList, activeProject, loadProject],
  );

  const handleDuplicateSpecificScene = useCallback(
    async (sceneId: string) => {
      if (!activeProject) {
        logger.error("[CanvasRecorder] Cannot duplicate scene: No active project");
        return;
      }
      
      // Check scene limit for recording mode (not blank mode)
      if (!canAddScene(sceneList.length, isBlankMode)) {
        notification.warning({
          message: 'Scene Limit Reached',
          description: `Maximum ${PROJECT_LIMITS.MAX_SCENES_RECORDING_MODE} scenes allowed per project in recording mode.`,
          placement: 'topRight',
        });
        return;
      }

      try {
        // Find scene in current scene list
        const scene = sceneList.find((s) => s.id === sceneId);
        if (!scene) {
          throw new Error(`Scene not found: ${sceneId}`);
        }

        const isCloudProject = !isNaN(Number(activeProject.id));
        const newOrder = scene.order + 1;

        logger.info("[CanvasRecorder] Duplicating scene:", {
          sceneId,
          projectId: activeProject.id,
          title: scene.title,
          originalOrder: scene.order,
          newOrder
        });

        // For cloud projects, we need to shift all subsequent scenes first
        if (isCloudProject) {
          // Update order of all scenes that come after the original
          const scenesToShift = sceneList.filter(s => s.order >= newOrder);
          for (const sceneToShift of scenesToShift) {
            await SceneService.updateScene(
              sceneToShift.id,
              { order: sceneToShift.order + 1 },
              activeProject.id
            );
          }
        }

        // Create new scene data (without videoUrl to ensure fresh recording)
        const newSceneData: Omit<Scene, 'id'> = {
          title: `${scene.title} (Copy)`,
          order: newOrder,
          layout: scene.layout ? [...scene.layout] : [],
          background: scene.background ? { ...scene.background } : { type: 'solid', colors: ['#1F2937'] },
          script: scene.script || "",
          duration: 0, // Reset duration for new recording
          videoChunks: [],
          status: "pending" as const,
          layoutPreset: scene.layoutPreset,
          transitionOut: scene.transitionOut || getDefaultTransition("cut"),
          tags: scene.tags || [],
          notes: scene.notes || "",
        };

        // Add scene to project (handles both cloud and local projects)
        await SceneService.addSceneToProject(activeProject.id, newSceneData);

        // For local projects, reorder scenes in memory
        if (!isCloudProject) {
          const project = await SceneService.getProjectById(activeProject.id);
          if (project) {
            // Sort scenes by order
            project.scenes.sort((a, b) => a.order - b.order);
            await SceneService.updateProject(activeProject.id, { scenes: project.scenes });
          }
        }

        // Reload the project to update the scene list
        const updatedProject = await SceneService.getProjectById(
          activeProject.id,
        );
        if (updatedProject) {
          loadProject(updatedProject);
        }

        logger.info("[CanvasRecorder] Scene duplicated successfully at position", newOrder);
      } catch (error) {
        logger.error("[CanvasRecorder] Failed to duplicate scene:", error);
      }
    },
    [activeProject, sceneList, loadProject, isBlankMode],
  );

  // Show delete confirmation modal
  const handleDeleteScene = useCallback(
    (sceneId: string) => {
      setSceneToDelete(sceneId);
      setIsDeleteSceneModalVisible(true);
    },
    [],
  );

  // Actually delete the scene with proper cleanup
  const confirmDeleteScene = useCallback(
    async () => {
      if (!activeProject || !sceneToDelete) return;

      const scene = sceneList.find((s) => s.id === sceneToDelete);
      if (!scene) return;

      setIsDeletingScene(true);

      try {
        // Check if this is a cloud project (numeric ID)
        const isCloudProject = !isNaN(Number(activeProject.id));

        if (isCloudProject) {
          // For cloud projects, delete from PostgreSQL
          const numericSceneId = Number(sceneToDelete);
          await projectsManager.deleteScene(numericSceneId);
          
          // Clean up any pending chunks in IndexedDB
          try {
            await SceneService.deleteSceneChunks(sceneToDelete);
          } catch (error) {
            logger.warn("[CanvasRecorder] No chunks to clean up for scene:", sceneToDelete);
          }
        } else {
          // For local projects, clean up chunks first
          try {
            await SceneService.deleteSceneChunks(sceneToDelete);
          } catch (error) {
            logger.warn("[CanvasRecorder] No chunks to clean up for scene:", sceneToDelete);
          }
          
          // Then delete the scene from IndexedDB
          await SceneService.removeSceneFromProject(activeProject.id, sceneToDelete);
          await SceneService.deleteScene(sceneToDelete);
        }

        // Reload the project to update the scene list (merges with pending_upload scenes)
        const updatedProject = await SceneService.getProjectById(
          activeProject.id,
        );
        if (updatedProject) {
          loadProject(updatedProject);
        }

        logger.info("[CanvasRecorder] Scene deleted successfully:", sceneToDelete);
        notification.success({
          message: 'Scene Deleted',
          description: `${scene.title} has been deleted successfully.`,
          placement: 'topRight',
        });
      } catch (error) {
        logger.error("[CanvasRecorder] Failed to delete scene:", error);
        notification.error({
          message: 'Deletion Failed',
          description: 'Failed to delete the scene. Please try again.',
          placement: 'topRight',
        });
      } finally {
        setIsDeletingScene(false);
        setIsDeleteSceneModalVisible(false);
        setSceneToDelete(null);
      }
    },
    [activeProject, sceneList, sceneToDelete, loadProject],
  );

  const handleReorderScenes = useCallback(
    async (sceneId: string, newIndex: number) => {
      if (!activeProject) {
        return;
      }

      try {
        const oldIndex = sceneList.findIndex((s) => s.id === sceneId);

        if (oldIndex === -1) {
          return;
        }

        if (oldIndex === newIndex) {
          return;
        }

        // Immutable reorder algorithm
        const reorderedScenes = [...sceneList];
        const [removed] = reorderedScenes.splice(oldIndex, 1);
        reorderedScenes.splice(newIndex, 0, removed);

        const scenesWithNewOrder = reorderedScenes.map((scene, index) => ({
          ...scene,
          order: index,
        }));

        // OPTIMISTIC UPDATE: Update UI immediately
        const updatedProject = {
          ...activeProject,
          scenes: scenesWithNewOrder,
        };
        loadProject(updatedProject);

        // Update IndexedDB
        for (const scene of scenesWithNewOrder) {
          await SceneService.updateScene(scene.id, { order: scene.order });
        }

        // Update PostgreSQL backend via API
        const { projectsManager } = await import("./services/projectsManager");
        for (const scene of scenesWithNewOrder) {
          try {
            await projectsManager.updateScene(parseInt(scene.id), {
              sceneOrder: scene.order,
            });
          } catch (error) {
            logger.error(`Failed to update backend for scene ${scene.id}:`, error);
          }
        }

        logger.info("[CanvasRecorder] Scenes reordered successfully");
      } catch (error) {
        logger.error("[CanvasRecorder] Failed to reorder scenes:", error);
      }
    },
    [activeProject, sceneList, loadProject],
  );

  // Slides layout enhanced handlers
  const handleSceneTitleChange = useCallback(
    async (sceneId: string, newTitle: string) => {
      setIsUpdatingScene(true);
      try {
        await SceneService.updateScene(sceneId, { title: newTitle }, activeProject!.id);
        const updatedProject = await SceneService.getProjectById(
          activeProject!.id,
        );
        if (updatedProject) {
          loadProject(updatedProject);
        }
        logger.info("[CanvasRecorder] Scene title updated:", sceneId);
      } catch (error) {
        logger.error("[CanvasRecorder] Failed to update scene title:", error);
      } finally {
        setIsUpdatingScene(false);
      }
    },
    [activeProject, loadProject],
  );

  const handleSceneTagsChange = useCallback(
    async (sceneId: string, tags: any[]) => {
      setIsUpdatingScene(true);
      try {
        await SceneService.updateScene(sceneId, { tags }, activeProject!.id);
        const updatedProject = await SceneService.getProjectById(
          activeProject!.id,
        );
        if (updatedProject) {
          loadProject(updatedProject);
        }
        logger.info("[CanvasRecorder] Scene tags updated:", sceneId, tags);
      } catch (error) {
        logger.error("[CanvasRecorder] Failed to update scene tags:", error);
      } finally {
        setIsUpdatingScene(false);
      }
    },
    [activeProject, loadProject],
  );

  const handleProjectTitleChange = useCallback(
    async (projectId: string, newTitle: string) => {
      if (!activeProject || !newTitle.trim()) return;

      setIsUpdatingScene(true);
      try {
        // Update via projectsManager for cloud projects (numeric ID)
        if (!isNaN(Number(projectId))) {
          await projectsManager.updateProject(Number(projectId), { name: newTitle.trim() });
          const updatedProject = await SceneService.getProjectById(projectId);
          if (updatedProject) {
            loadProject(updatedProject);
          }
        } else {
          // Update local project via SceneService
          await SceneService.updateProject(projectId, { title: newTitle.trim() });
          const updatedProject = await SceneService.getProjectById(projectId);
          if (updatedProject) {
            loadProject(updatedProject);
          }
        }
      } catch (error) {
        logger.error("[CanvasRecorder] Failed to update project title:", error);
      } finally {
        setIsUpdatingScene(false);
      }
    },
    [activeProject, loadProject],
  );

  const handleSceneNotesChange = useCallback(
    async (sceneId: string, notes: string) => {
      setIsUpdatingScene(true);
      try {
        await SceneService.updateScene(sceneId, { notes }, activeProject!.id);
        const updatedProject = await SceneService.getProjectById(
          activeProject!.id,
        );
        if (updatedProject) {
          loadProject(updatedProject);
        }
        logger.info("[CanvasRecorder] Scene notes updated:", sceneId);
      } catch (error) {
        logger.error("[CanvasRecorder] Failed to update scene notes:", error);
      } finally {
        setIsUpdatingScene(false);
      }
    },
    [activeProject, loadProject],
  );

  // Slides layout modal handlers
  const handleCopySettings = useCallback(
    (sceneId: string) => {
      const scene = sceneList.find((s) => s.id === sceneId);
      if (scene) {
        setCopySettingsSourceScene(scene);
        setCopySettingsModalVisible(true);
        logger.info(
          "[CanvasRecorder] Copy settings modal opened for scene:",
          sceneId,
        );
      }
    },
    [sceneList],
  );

  const handleCopySettingsConfirm = useCallback(
    async (targetSceneIds: string[], settings: string[]) => {
      if (!copySettingsSourceScene || !activeProject) return;

      try {
        const sourceScene = copySettingsSourceScene;

        for (const targetSceneId of targetSceneIds) {
          const updates: Partial<Scene> = {};

          if (settings.includes("layout") && sourceScene.layoutPreset) {
            updates.layoutPreset = sourceScene.layoutPreset;
          }
          if (settings.includes("background") && sourceScene.background) {
            updates.background = sourceScene.background;
          }
          if (settings.includes("script")) {
            updates.script = sourceScene.script;
          }
          if (settings.includes("duration")) {
            updates.suggestedDuration = sourceScene.suggestedDuration;
          }
          if (settings.includes("tags")) {
            updates.tags = sourceScene.tags;
          }
          if (settings.includes("transition") && sourceScene.transition) {
            updates.transition = sourceScene.transition;
          }

          await SceneService.updateScene(targetSceneId, updates, activeProject.id);
        }

        const updatedProject = await SceneService.getProjectById(
          activeProject.id,
        );
        if (updatedProject) {
          loadProject(updatedProject);
        }

        setCopySettingsModalVisible(false);
        logger.info(
          "[CanvasRecorder] Settings copied to",
          targetSceneIds.length,
          "scenes",
        );
      } catch (error) {
        logger.error("[CanvasRecorder] Failed to copy settings:", error);
      }
    },
    [copySettingsSourceScene, activeProject, loadProject],
  );

  const handleJumpToRecording = useCallback(
    (sceneId: string) => {
      const scene = sceneList.find((s) => s.id === sceneId);
      if (scene) {
        setCurrentScene(scene);
        const sceneIndex = sceneList.findIndex((s) => s.id === sceneId);
        if (sceneIndex !== -1) {
          setCurrentSceneIndex(sceneIndex);
        }
        logger.info("[CanvasRecorder] Jumped to recording for scene:", sceneId);
      }
    },
    [sceneList, setCurrentScene, setCurrentSceneIndex],
  );

  const handleRetakeScene = useCallback(
    async (sceneId: string) => {
      logger.info(`[CanvasRecorder] 🔄 handleRetakeScene called for sceneId: ${sceneId}`);
      const scene = sceneList.find((s) => s.id === sceneId);
      
      if (!scene) {
        logger.warn(`[CanvasRecorder] ❌ Scene not found: ${sceneId}`);
        return;
      }

      logger.info(`[CanvasRecorder] 📋 Found scene: ${scene.title} (status: ${scene.status}, videoGuid: ${scene.videoGuid})`);

      try {
        // IMPORTANT: DO NOT delete old video/thumbnail yet!
        // They will be deleted when the new recording is accepted
        // This allows the user to see the old thumbnail while recording a new version
        
        // Only clean up IndexedDB chunks (not S3 files)
        if (scene.videoGuid) {
          logger.info(`[CanvasRecorder] 🗑️ Deleting IndexedDB chunks for old videoGuid: ${scene.videoGuid}`);
          const chunks = await db.chunks
            .where('videoGuid')
            .equals(scene.videoGuid)
            .toArray();
          
          for (const chunk of chunks) {
            await db.chunks.delete(chunk.id);
          }
          
          logger.info(`[CanvasRecorder] ✅ Deleted ${chunks.length} IndexedDB chunks for videoGuid: ${scene.videoGuid}`);
        }
        
        // Mark scene as pending AND clear video data from database
        logger.info(`[CanvasRecorder] 💾 Clearing scene for retake...`, {
          sceneId,
          activeProjectId: activeProject?.id,
          activeProjectExists: !!activeProject,
          isCloudProject: activeProject?.id && !isNaN(Number(activeProject.id))
        });
        
        await SceneService.updateScene(sceneId, {
          status: 'pending',
          videoUrl: null,
          thumbnailUrl: null,
          videoGuid: null,
          duration: 0,
          recordedAt: null,
          analysisStatus: 'pending', // Reset analysis status for re-recording
        }, activeProject?.id);
        
        logger.info(`[CanvasRecorder] ✅ Scene cleared for retake in database`);
        
        // CRITICAL: Set as current scene so recording uses scene mode (not single mode)
        setCurrentScene(scene);
        const sceneIndex = sceneList.findIndex((s) => s.id === sceneId);
        if (sceneIndex !== -1) {
          setCurrentSceneIndex(sceneIndex);
        }
        logger.info(`[CanvasRecorder] ✅ Current scene set to: ${scene.title}`);
        
        // Refresh scene list from database to get updated state (cleared video/thumbnail)
        if (activeProject?.id && !isNaN(Number(activeProject.id))) {
          const { projectsManager } = await import('./services/projectsManager');
          const freshProject: any = await projectsManager.getProject(Number(activeProject.id));
          
          if (freshProject.scenes) {
            const updatedSceneList = sceneList.map(s => {
              const freshScene = freshProject.scenes.find((fs: any) => fs.id === Number(s.id));
              return freshScene ? {
                ...s,
                status: freshScene.status as Scene['status'],
                videoUrl: freshScene.videoUrl,
                thumbnailUrl: freshScene.thumbnailUrl,
                duration: freshScene.duration
              } : s;
            });
            
            // Update state atoms
            loadProject({
              ...activeProject,
              scenes: updatedSceneList
            });
            
            logger.info(`[CanvasRecorder] ✅ Scene list refreshed with cleared data`);
          }
        }
        
        logger.info(`[CanvasRecorder] ✨ Scene reset for retake complete: ${scene.title}`);
        
        // Auto-start recording with countdown
        setTimeout(() => {
          logger.info(`[CanvasRecorder] 🎬 Auto-starting recording for retake, sceneId: ${sceneId}`);
          
          // Show countdown
          setShowCountdown(true);
          
          // Start recording after countdown (3 seconds)
          // CRITICAL FIX: Pass sceneId to ensure correct scene is captured
          setTimeout(() => {
            setShowCountdown(false);
            startRecording(false, sceneId);
            logger.info(`[CanvasRecorder] ▶️ Recording started automatically after retake for scene: ${sceneId}`);
          }, 3000);
        }, 500);
      } catch (error) {
        logger.error("[CanvasRecorder] ❌ Failed to retake scene:", error);
        Modal.error({
          title: 'Error',
          content: 'Failed to reset scene for re-recording. Please try again.',
        });
      }
    },
    [sceneList, activeProject, setCurrentScene, setCurrentSceneIndex, loadProject, setShowCountdown, startRecording],
  );

  const handleEditSettings = useCallback(
    (sceneId: string) => {
      const scene = sceneList.find((s) => s.id === sceneId);
      if (scene) {
        setEditSettingsScene(scene);
        setEditSettingsModalVisible(true);
        logger.info(
          "[CanvasRecorder] Edit settings modal opened for scene:",
          sceneId,
        );
      }
    },
    [sceneList],
  );

  const handleEditSettingsConfirm = useCallback(
    async (sceneId: string, updates: Partial<Scene>) => {
      try {
        await SceneService.updateScene(sceneId, updates, activeProject!.id);
        const updatedProject = await SceneService.getProjectById(
          activeProject!.id,
        );
        if (updatedProject) {
          loadProject(updatedProject);
        }
        setEditSettingsModalVisible(false);
        logger.info("[CanvasRecorder] Scene settings updated:", sceneId);
      } catch (error) {
        logger.error(
          "[CanvasRecorder] Failed to update scene settings:",
          error,
        );
      }
    },
    [activeProject, loadProject],
  );

  const handleExportScene = useCallback(async (sceneId: string) => {
    const scene = sceneList.find((s) => s.id === sceneId);
    if (!scene || !scene.videoUrl) {
      logger.error("[CanvasRecorder] Cannot export scene: No video found");
      return;
    }

    try {
      logger.info("[CanvasRecorder] Exporting scene:", {
        sceneId,
        title: scene.title,
        videoUrl: scene.videoUrl
      });

      // Fetch the video file
      const response = await fetch(scene.videoUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch video: ${response.statusText}`);
      }

      const blob = await response.blob();
      
      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename from scene title
      const filename = `${scene.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${Date.now()}.webm`;
      link.download = filename;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      logger.info("[CanvasRecorder] Scene exported successfully:", filename);
    } catch (error) {
      logger.error("[CanvasRecorder] Failed to export scene:", error);
    }
  }, [sceneList]);

  const handleResetVideo = useCallback(async (sceneId: string) => {
    const scene = sceneList.find((s) => s.id === sceneId);
    if (!scene) {
      logger.error("[CanvasRecorder] Cannot reset video: Scene not found");
      return;
    }

    // Allow reset for both 'recorded' and 'pending_upload' scenes
    if (scene.status !== 'recorded' && scene.status !== 'pending_upload') {
      Modal.warning({
        title: 'No Recording',
        content: 'This scene has not been recorded yet.',
        okButtonProps: {
          style: { background: '#4A3CFA' }
        }
      });
      return;
    }

    Modal.confirm({
      title: 'Reset Scene Video?',
      content: `This will delete the recorded video for "${scene.title}" but keep all scene settings (script, background, layout). You can record this scene again. Are you sure?`,
      okText: 'Reset Video',
      okType: 'danger',
      cancelText: 'Cancel',
      okButtonProps: {
        danger: true
      },
      onOk: async () => {
        try {
          logger.info('[CanvasRecorder] Resetting scene video:', sceneId);
          
          // Extract project ID from nested structure
          const actualProject = (activeProject as any)?.project || activeProject;
          const projectId = actualProject?.id;
          
          // Check if this is a cloud project (numeric ID)
          const isCloudProject = projectId && !isNaN(Number(projectId));
          
          // Delete video chunks from IndexedDB - comprehensive cleanup
          let totalDeleted = 0;
          
          // Method 1: Delete by videoChunks array
          if (scene.videoChunks && scene.videoChunks.length > 0) {
            await Promise.all(
              scene.videoChunks.map(chunkId => db.chunks.delete(chunkId))
            );
            totalDeleted += scene.videoChunks.length;
            logger.info('[CanvasRecorder] Deleted video chunks from videoChunks array:', scene.videoChunks.length);
          }
          
          // Method 2: Delete by videoGuid (for confirmed scenes)
          if (scene.videoGuid) {
            const chunksByGuid = await db.chunks
              .where('videoGuid')
              .equals(scene.videoGuid)
              .toArray();
            
            for (const chunk of chunksByGuid) {
              await db.chunks.delete(chunk.id);
            }
            totalDeleted += chunksByGuid.length;
            logger.info('[CanvasRecorder] Deleted video chunks by videoGuid:', chunksByGuid.length);
          }
          
          // Method 3: Delete by sceneId (for pending_upload scenes)
          const chunksBySceneId = await db.chunks
            .where('sceneId')
            .equals(String(sceneId))
            .toArray();
          
          if (chunksBySceneId.length > 0) {
            for (const chunk of chunksBySceneId) {
              await db.chunks.delete(chunk.id);
            }
            totalDeleted += chunksBySceneId.length;
            logger.info('[CanvasRecorder] Deleted video chunks by sceneId:', chunksBySceneId.length);
          }
          
          logger.info('[CanvasRecorder] Total chunks deleted from all methods:', totalDeleted);
          
          if (isCloudProject) {
            const numericSceneId = Number(sceneId);
            const updatePayload = {
              status: 'pending',
              videoUrl: null,
              thumbnailUrl: null,
              duration: 0,
              recordedAt: null,
              videoGuid: null,
              analysisStatus: 'pending',
            };
            logger.info('[CanvasRecorder] Resetting scene - payload being sent:', updatePayload);
            await projectsManager.updateScene(numericSceneId, updatePayload);
            
            const updatedProject = await SceneService.getProjectById(projectId!);
            if (updatedProject) {
              loadProject(updatedProject);
              
              const freshScene = updatedProject.scenes.find((s: any) => String(s.id) === String(sceneId));
              if (freshScene && String(currentScene?.id) === String(sceneId)) {
                setCurrentScene(freshScene);
                logger.info('[CanvasRecorder] Updated currentScene atom after reset:', {
                  sceneId,
                  status: freshScene.status,
                  layoutPreset: freshScene.layoutPreset,
                  hasVideoUrl: !!freshScene.videoUrl
                });
              }
            }
          } else {
            await SceneService.updateScene(
              sceneId,
              {
                status: 'pending' as const,
                videoUrl: undefined,
                thumbnailUrl: undefined,
                videoChunks: [],
                duration: 0,
                recordedAt: undefined,
                videoGuid: undefined,
                analysisStatus: 'pending',
              },
              projectId
            );
            
            const updatedProject = await SceneService.getProjectById(projectId!);
            if (updatedProject) {
              loadProject(updatedProject);
              
              const freshScene = updatedProject.scenes.find((s: any) => String(s.id) === String(sceneId));
              if (freshScene && currentScene?.id === sceneId) {
                setCurrentScene(freshScene);
                logger.info('[CanvasRecorder] Updated currentScene atom after reset:', {
                  sceneId,
                  status: freshScene.status,
                  layoutPreset: freshScene.layoutPreset,
                  hasVideoUrl: !!freshScene.videoUrl
                });
              }
            }
          }
          
          logger.info('[CanvasRecorder] Scene video reset successfully');
          
          Modal.success({
            title: 'Video Reset',
            content: `The recorded video for "${scene.title}" has been deleted. All scene settings have been preserved.`,
            okButtonProps: {
              style: { background: '#4A3CFA' }
            }
          });
        } catch (error) {
          logger.error('[CanvasRecorder] Failed to reset scene video:', error);
          Modal.error({
            title: 'Reset Failed',
            content: `Failed to reset scene video: ${error instanceof Error ? error.message : 'Unknown error'}`,
          });
        }
      }
    });
  }, [sceneList, activeProject, loadProject]);

  const handlePreviewScene = useCallback(
    (sceneId: string) => {
      const scene = sceneList.find((s) => String(s.id) === String(sceneId));
      if (scene) {
        setScenePreviewScene(scene);
        setScenePreviewModalVisible(true);
      }
    },
    [sceneList],
  );

  // Phase 4.3: Handler for "Confirm Scene" - opens RecordingPreviewModal for pending_upload/pending_confirmation scenes
  const [, showRecordingPreview] = useJotaiAtom(showRecordingPreviewAtom);
  
  const handleConfirmScene = useCallback(
    async (sceneId: string) => {
      const scene = sceneList.find((s) => s.id === sceneId);
      if (!scene) {
        logger.error('[CanvasRecorder] Scene not found for confirm:', sceneId);
        return;
      }

      logger.info('[CanvasRecorder] 🔄 Confirming scene (reopening RecordingPreviewModal):', {
        sceneId,
        title: scene.title,
        status: scene.status,
        hasVideoUrl: !!scene.videoUrl,
      });

      // For pending_confirmation scenes, use the existing videoUrl (already uploaded to temp storage)
      if (scene.status === 'pending_confirmation' && scene.videoUrl) {
        logger.info('[CanvasRecorder] 📹 Using existing videoUrl for pending_confirmation scene:', scene.videoUrl);
        
        // Open RecordingPreviewModal with the existing video URL
        showRecordingPreview({
          mode: 'scene',
          videoGuid: undefined, // No videoGuid needed, will use scene.videoUrl
          sceneId,
          scene,
          duration: scene.duration || 0,
          s3Key: scene.videoUrl, // Pass the S3 URL directly
          micEnabled: scene.micEnabled ?? false, // Pass micEnabled from scene
        });
        return;
      }

      // Check if scene already has recorded separate streams data (already uploaded)
      const sceneMetadata = scene.metadata as any;
      if (sceneMetadata?.streams && (sceneMetadata.streams.screen?.segments?.[0]?.key || sceneMetadata.streams.webcam?.segments?.[0]?.key)) {
        const streamUrl = sceneMetadata.streams.screen?.segments?.[0]?.key || sceneMetadata.streams.webcam?.segments?.[0]?.key;
        logger.info('[CanvasRecorder] 📹 Scene already has recorded separate streams, using existing URL:', {
          sceneId,
          status: scene.status,
          streamUrl: streamUrl?.substring(0, 50) + '...',
        });
        
        // Open RecordingPreviewModal with the existing stream URL
        showRecordingPreview({
          mode: 'scene',
          videoGuid: undefined,
          sceneId,
          scene,
          duration: scene.duration || sceneMetadata.duration || 0,
          s3Key: streamUrl,
          micEnabled: scene.micEnabled ?? sceneMetadata.micEnabled ?? false,
        });
        return;
      }

      // Check for pending separate stream recordings in IndexedDB first
      try {
        const pendingRow = await getPendingRecordingBySceneId(sceneId);
        if (pendingRow) {
          logger.info('[CanvasRecorder] 📹 Found pending separate stream recording in IndexedDB:', {
            sceneId,
            duration: pendingRow.duration,
            hasScreen: pendingRow.screenSegments?.length > 0,
            hasWebcam: pendingRow.webcamSegments?.length > 0,
          });
          
          // Create blob URLs from stored blobs
          let screenBlobUrl: string | null = null;
          let webcamBlobUrl: string | null = null;
          
          if (pendingRow.screenSegments?.length > 0 && pendingRow.screenSegments[0].blob) {
            screenBlobUrl = URL.createObjectURL(pendingRow.screenSegments[0].blob);
          }
          if (pendingRow.webcamSegments?.length > 0 && pendingRow.webcamSegments[0].blob) {
            webcamBlobUrl = URL.createObjectURL(pendingRow.webcamSegments[0].blob);
          }
          
          const combinedBlobUrl = screenBlobUrl || webcamBlobUrl;
          if (combinedBlobUrl) {
            // Store recovered data for the modal to use
            setRecoveredVideoUrl(combinedBlobUrl);
            setRecoveredPendingData(pendingRow);
            
            // Open RecordingPreviewModal with recovered recording
            showRecordingPreview({
              mode: 'scene',
              sceneId,
              scene,
              duration: pendingRow.duration,
              micEnabled: pendingRow.micEnabled,
            });
            return;
          } else {
            // No valid blobs, clean up
            await deletePendingRecording(pendingRow.id);
          }
        }
      } catch (error) {
        logger.warn('[CanvasRecorder] Error checking pending recordings:', error);
      }

      // For other scenes, get chunks from IndexedDB to get videoGuid
      const chunks = await db.chunks.toArray();
      const sceneChunks = chunks.filter(c => c.sceneId === sceneId);
      
      if (sceneChunks.length === 0) {
        logger.error('[CanvasRecorder] No chunks found for scene:', sceneId);
        Modal.error({
          title: 'No Recording Found',
          content: 'No recording chunks found for this scene. Please record the scene again.',
          okButtonProps: {
            type: 'primary',
            style: { background: '#4a3cfa' }
          }
        });
        return;
      }

      // Get the LATEST videoGuid (most recent recording) by sorting chunks by createdAt
      const sortedChunks = sceneChunks.sort((a, b) => b.createdAt - a.createdAt);
      const videoGuid = sortedChunks[0].videoGuid;
      
      logger.info('[CanvasRecorder] 📹 Confirm Scene - found chunks:', {
        sceneId,
        totalChunks: sceneChunks.length,
        videoGuid,
        latestChunkTime: new Date(sortedChunks[0].createdAt).toISOString(),
      });

      // Open RecordingPreviewModal
      showRecordingPreview({
        mode: 'scene',
        videoGuid,
        sceneId,
        scene,
        duration: scene.duration || 0,
        micEnabled: scene.micEnabled ?? false, // Pass micEnabled from scene
      });
    },
    [sceneList, showRecordingPreview],
  );

  const handleScenePreviewRetake = useCallback(
    (sceneId: string) => {
      logger.info("[CanvasRecorder] 🔄 Preview modal retake clicked for scene:", sceneId);
      setScenePreviewModalVisible(false); // Close preview modal first
      handleRetakeScene(sceneId); // Use full retake flow with auto-start
    },
    [handleRetakeScene],
  );

  const handleScenePreviewDownload = useCallback(
    (sceneId: string) => {
      const scene = sceneList.find((s) => s.id === sceneId);
      if (scene && scene.videoChunks) {
        downloadChunkedBlob(scene.videoChunks, `${scene.title}.webm`);
        logger.info("[CanvasRecorder] Downloaded scene:", sceneId);
      }
    },
    [sceneList],
  );

  // Unified transition handler - opens TransitionStudioModal for both edit and preview
  const handleOpenTransitionStudio = useCallback(
    (fromSceneIndex: number) => {
      logger.info(
        "[CanvasRecorder] Opening transition studio for scene index",
        fromSceneIndex,
      );
      const fromScene = sceneList[fromSceneIndex];
      if (fromScene) {
        setTransitionStudioSceneId(fromScene.id);
      }
    },
    [sceneList],
  );

  const handleTransitionChange = useCallback(
    async (sceneId: string, transition: any) => {
      try {
        console.log("🔄 [handleTransitionChange] Saving transition for scene:", sceneId, "Transition:", transition);
        setSavingTransitionSceneId(sceneId); // Show loading indicator
        await SceneService.updateScene(sceneId, { transitionOut: transition }, activeProject!.id);
        console.log("✅ [handleTransitionChange] Scene updated successfully");
        const updatedProject = await SceneService.getProjectById(
          activeProject!.id,
        );
        console.log("📦 [handleTransitionChange] Reloaded project:", updatedProject);
        if (updatedProject) {
          loadProject(updatedProject);
        }
        logger.info("[CanvasRecorder] Transition updated for scene:", sceneId);
        setSavingTransitionSceneId(null); // Hide loading indicator
      } catch (error) {
        console.error("❌ [handleTransitionChange] Failed to update transition:", error);
        logger.error("[CanvasRecorder] Failed to update transition:", error);
        setSavingTransitionSceneId(null); // Hide loading indicator even on error
      }
    },
    [activeProject, loadProject],
  );

  const handleApplyTransitionToAll = useCallback(
    async (transition: any) => {
      try {
        for (const scene of sceneList) {
          await SceneService.updateScene(scene.id, { transitionOut: transition }, activeProject!.id);
        }
        const updatedProject = await SceneService.getProjectById(
          activeProject!.id,
        );
        if (updatedProject) {
          loadProject(updatedProject);
        }
        logger.info(
          "[CanvasRecorder] Transition applied to all scenes:",
          transition,
        );
        setTransitionStudioSceneId(null);
      } catch (error) {
        logger.error(
          "[CanvasRecorder] Failed to apply transition to all:",
          error,
        );
      }
    },
    [sceneList, activeProject, loadProject],
  );

  const handleApplyTransitionToRemaining = useCallback(
    async (sceneId: string, transition: any) => {
      try {
        const sceneIndex = sceneList.findIndex((s) => s.id === sceneId);
        if (sceneIndex >= 0) {
          for (let i = sceneIndex; i < sceneList.length; i++) {
            await SceneService.updateScene(sceneList[i].id, { transitionOut: transition }, activeProject!.id);
          }
          const updatedProject = await SceneService.getProjectById(
            activeProject!.id,
          );
          if (updatedProject) {
            loadProject(updatedProject);
          }
          logger.info(
            "[CanvasRecorder] Transition applied to remaining scenes:",
            transition,
          );
          setTransitionStudioSceneId(null);
        }
      } catch (error) {
        logger.error(
          "[CanvasRecorder] Failed to apply transition to remaining:",
          error,
        );
      }
    },
    [sceneList, activeProject, loadProject],
  );

  // Only apply default background in blank mode - for project mode, let useSceneSync handle it
  useCanvasBackground(isBlankModeFromUrl ? canvasBackgrounds[0] : undefined);
  
  // Stable callback for outside click handler - use useCallback to prevent re-registering listener on every render
  const handleOutsideClickCallback = useCallback(() => {
    setClickedOutside(true);
    setSelectedStream(null);
    // Deselect fabric canvas active object
    if (stageRef?.current) {
      const canvas = stageRef.current.getCanvas();
      if (canvas) {
        canvas.discardActiveObject();
        canvas.requestRenderAll();
      }
    }
  }, [stageRef, setClickedOutside, setSelectedStream]);
  
  // Use outside click hook - deselect all canvas layers when clicking outside
  useOutsideClick(recordingAreaRef, handleOutsideClickCallback);

  // FIX: Move useLayoutManager call earlier so applyPreset is available for handleLayoutPresetChange
  const { applyPreset } = useLayoutManager();

  // Sprint Enhancement: Scene synchronization for templates
  // FIXED: Wrapped callbacks in useCallback to prevent infinite loop
  const handleScriptChange = useCallback((script: string) => {
    if (script) {
      localStorage.setItem("teleprompter_text", script);
    } else {
      localStorage.removeItem("teleprompter_text");
    }
    window.dispatchEvent(
      new CustomEvent("teleprompter-script-update", { detail: { script: script || "" } }),
    );
  }, []);

  // Handle scene switch - check stream requirements even when preset is the same
  // The checkPresetStreamRequirements function itself handles whether to show modal
  // (it only shows if streams are actually missing)
  // NOTE: We show the modal even for recorded scenes so user can set up streams for other scenes
  const handleSceneSwitch = useCallback((scene: any) => {
    if (!scene || !recordingToolbarRef.current) return;
    
    // Skip if preview modal is open
    const previewModalOpen = recordingPreview?.visible === true;

    if (scene.layoutPreset === 'Preset_PresentationSlide') {
      recordingToolbarRef.current?.stopWebcam();
      recordingToolbarRef.current?.stopScreenShare();
      return;
    }
    
    if (!previewModalOpen && scene.layoutPreset) {
      setTimeout(() => {
        recordingToolbarRef.current?.checkPresetStreamRequirements(scene.layoutPreset);
      }, 200);
    }
  }, [recordingPreview?.visible]);

  const handleLayoutPresetChange = useCallback(
    async (presetName: string, options?: { fromSceneLoad?: boolean; preserveLayout?: boolean }) => {
      // Apply layout preset when scene changes (with safety checks)
      try {
        const fromSceneLoad = options?.fromSceneLoad === true;
        const preserveLayout = options?.preserveLayout === true;
        
        // Skip preset reapplication if we just did a quality-triggered scale
        // This prevents the preset from overwriting the scaled layout
        if (justScaledForQualityRef.current && fromSceneLoad) {
          return;
        }
        
        // FIX: When loading from scene, check if canvas dimensions match project aspect ratio
        // If not, DEFER until canvas dimensions have been updated by AspectRatioSync
        if (fromSceneLoad && activeProject?.aspectRatio) {
          const projectRatio = activeProject.aspectRatio;
          const currentRatio = canvasRatio;
          
          // Check if canvas ratio doesn't match project ratio yet (race condition)
          if (projectRatio !== currentRatio) {
            // Defer - canvas ratio doesn't match project ratio yet
            return;
          }
          
          // Also verify that canvas dimensions are consistent with the ratio
          const isPortrait = projectRatio === '9:16';
          const dimensionsCorrect = isPortrait 
            ? canvasWidth < canvasHeight 
            : canvasWidth >= canvasHeight;
          
          if (!dimensionsCorrect) {
            // Defer - dimensions not yet updated to match aspect ratio
            return;
          }
        }
        
        // For scene load: Apply preset first, THEN check for missing streams to show modal
        // For user clicks: Check streams first, block if not ready
        if (!fromSceneLoad && recordingToolbarRef.current) {
          const streamsReady = recordingToolbarRef.current.checkPresetStreamRequirements(presetName);
          if (!streamsReady) {
            return; // Modal is shown, preset will be applied after streams are ready
          }
        }

        const presets = await import("./presets/layoutPresets");
        // Look for preset in all preset groups (landscape, portrait, square) and top-level exports
        let presetFn = (presets as any)[presetName];
        if (!presetFn) presetFn = (presets.landscapePresets as any)?.[presetName];
        if (!presetFn) presetFn = (presets.portraitPresets as any)?.[presetName];
        if (!presetFn) presetFn = (presets.squarePresets as any)?.[presetName];

        if (presetFn && typeof presetFn === "function") {
          // SAFETY CHECK: Get aspect ratio with fallback, don't require stream
          const screenAspectRatio =
            screenStream?.getVideoTracks()?.[0]?.getSettings()?.aspectRatio ||
            16 / 9;

          // Apply synchronously now that we've verified dimensions are correct
          // When preserveLayout is true (quality change), preserve manual modifications
          applyPreset(
            presetFn,
            screenAspectRatio,
            canvasWidth,
            canvasHeight,
            canvasRatio,
            roundedWebcam,
            preserveLayout, // Preserve manual modifications when quality changes
          );
          // Sprint 19: Also update the selectedPreset state so UI reflects current preset
          setSelectedPreset(presetName);
          
          // For scene load: After applying preset, check if streams are missing and show modal
          // This allows the preset to be stored while still prompting for missing streams
          // Skip stream check for already recorded scenes (pending_confirmation, pending_upload or recorded)
          // and when preview modal is open
          const sceneAlreadyRecorded = currentScene?.status === 'pending_confirmation' || currentScene?.status === 'pending_upload' || currentScene?.status === 'recorded';
          const previewModalOpen = recordingPreview?.visible === true;
          
          if (fromSceneLoad && recordingToolbarRef.current && !sceneAlreadyRecorded && !previewModalOpen) {
            // Small delay to ensure preset is applied first
            setTimeout(() => {
              recordingToolbarRef.current?.checkPresetStreamRequirements(presetName);
            }, 200);
          }
        }
      } catch (error) {
        logger.error("[CanvasRecorder] Failed to apply layout preset:", error);
      }
    },
    [screenStream, canvasWidth, canvasHeight, canvasRatio, roundedWebcam, setSelectedPreset, activeProject?.aspectRatio, applyPreset, currentScene?.status, currentScene?.id, recordingPreview?.visible],
  );

  // Ref to track recording state for use inside requestAnimationFrame callback in handleBackgroundChange
  const bgSkipRecordingRef = useRef(isRecording);
  const bgSkipPendingRef = useRef(recordingPending);
  const bgSkipCountdownRef = useRef(showCountdown);
  
  // Keep refs in sync with state
  bgSkipRecordingRef.current = isRecording;
  bgSkipPendingRef.current = recordingPending;
  bgSkipCountdownRef.current = showCountdown;
  
  const handleBackgroundChange = useCallback(
    (background: any) => {
      // CRITICAL: Skip background changes during recording to prevent resets
      // Check both state (for immediate calls) and refs (for deferred calls via rAF)
      if (bgSkipRecordingRef.current || bgSkipPendingRef.current || bgSkipCountdownRef.current) {
        console.log('[handleBackgroundChange] SKIPPED - recording in progress', {
          isRecording: bgSkipRecordingRef.current,
          recordingPending: bgSkipPendingRef.current,
          showCountdown: bgSkipCountdownRef.current,
        });
        return;
      }
      
      // Convert scene background to CanvasBackground format and apply
      // Use requestAnimationFrame to prevent blocking
      requestAnimationFrame(() => {
        // Double-check recording state inside rAF since it's deferred
        if (bgSkipRecordingRef.current || bgSkipPendingRef.current || bgSkipCountdownRef.current) {
          return;
        }
        
        if (background.type === "color" && background.value) {
          setBackgroundColor(background.value);
          setBackgroundImage(null);
        } else if (
          background.type === "solid" &&
          background.colors &&
          background.colors[0]
        ) {
          setBackgroundColor(background.colors[0]);
          setBackgroundImage(null);
        } else if (
          background.type === "gradient" &&
          background.colors &&
          background.colors[0]
        ) {
          const firstColor = background.colors[0];
          let gradientCss: string;
          
          if (typeof firstColor === 'string' && firstColor.includes('gradient')) {
            gradientCss = firstColor;
          } else if (background.colors.length >= 2) {
            gradientCss = `linear-gradient(135deg, ${background.colors.join(", ")})`;
          } else {
            gradientCss = firstColor;
          }
          
          setBackgroundColor(gradientCss);
          setBackgroundImage(null);
        } else if (background.type === "image" && background.imageUrl) {
          // S3 presigned URLs lack CORS headers, so loading with crossOrigin="anonymous" fails.
          // Solution: Use server proxy via fetch() to get the image as a blob, then create a
          // blob URL which is same-origin and safe for canvas use.
          const s3Key = background.s3Key || background.value;
          const isBlobUrl = background.imageUrl.startsWith('blob:');
          const isLocalPath = background.imageUrl.startsWith('/');
          const useProxy = !!s3Key && !isBlobUrl && !isLocalPath;

          const bgS3Key = background.s3Key || background.imageKey || background.value;
          setIntendedBackgroundImage(background.imageUrl, bgS3Key);

          const loadImageFromUrl = (url: string, crossOrigin: boolean) => {
            const img = new Image();
            if (crossOrigin) {
              img.crossOrigin = "anonymous";
            }
            img.src = url;
            img.onload = () => {
              if (bgSkipRecordingRef.current || bgSkipPendingRef.current || bgSkipCountdownRef.current) {
                return;
              }
              setBackgroundImage(img);
              setBackgroundColor(null);
            };
            img.onerror = (err) => {
              console.error('[handleBackgroundChange] Failed to load background image:', err);
            };
          };

          if (useProxy) {
            const proxyUrl = `/api/s3-image-proxy?key=${encodeURIComponent(s3Key)}`;
            fetch(proxyUrl)
              .then(response => {
                if (!response.ok) throw new Error(`Proxy returned ${response.status}`);
                return response.blob();
              })
              .then(blob => {
                const blobUrl = URL.createObjectURL(blob);
                if (bgSkipRecordingRef.current || bgSkipPendingRef.current || bgSkipCountdownRef.current) {
                  URL.revokeObjectURL(blobUrl);
                  return;
                }
                loadImageFromUrl(blobUrl, false);
              })
              .catch(err => {
                console.error('[handleBackgroundChange] Proxy failed, falling back to direct:', err);
                loadImageFromUrl(background.imageUrl, !isBlobUrl);
              });
          } else {
            loadImageFromUrl(background.imageUrl, !isBlobUrl);
          }
        }
      });
    },
    [setBackgroundColor, setBackgroundImage, setIntendedBackgroundImage],
  );

  // Track the aspect ratio when layout was last applied to detect mismatches
  const layoutAspectRatioRef = useRef<string | undefined>(undefined);
  
  // CRITICAL FIX: Handle direct layout loading (bypasses preset application to prevent duplication)
  // Also validates that saved layout matches current canvas aspect ratio
  const handleLayoutChange = useCallback(
    (layout: any[]) => {
      // If aspect ratio changed since layout was saved, skip loading saved layout
      // This forces the preset to be applied instead, recalculating for current canvas
      if (layoutAspectRatioRef.current && layoutAspectRatioRef.current !== canvasRatio) {
        return;
      }
      
      // Validate layout dimensions against current canvas to detect aspect ratio mismatches
      // If any layout item has dimensions that exceed the canvas, skip loading (allow preset to be applied)
      const isLayoutValidForCanvas = layout.every((item) => {
        const itemWidth = item.width || 0;
        const itemHeight = item.height || 0;
        return itemWidth <= canvasWidth * 1.1 && itemHeight <= canvasHeight * 1.1;
      });
      
      if (!isLayoutValidForCanvas && layout.length > 0) {
        return;
      }
      
      // Apply deduplication before setting layout (check by stream type: "webcam" or "screen")
      const seenStreams = new Set<string>();
      const uniqueLayout = layout.filter((item) => {
        if (seenStreams.has(item.stream)) {
          return false;
        }
        seenStreams.add(item.stream);
        return true;
      });
      
      setInitialStreamLayout(uniqueLayout);
      layoutAspectRatioRef.current = canvasRatio;
    },
    [setInitialStreamLayout, canvasRatio, canvasWidth, canvasHeight],
  );

  // Handler to reset background to scene's default
  const handleResetBackground = useCallback(() => {
    if (!currentScene) return;
    
    // Get the scene's original background from metadata or direct property
    const sceneBackground = currentScene.background || (currentScene as any).metadata?.background;
    
    if (sceneBackground) {
      handleBackgroundChange(sceneBackground);
    } else {
      // Fallback to default solid color if no background defined
      setBackgroundColor('#1F2937');
      setBackgroundImage(null);
    }
  }, [currentScene, handleBackgroundChange, setBackgroundColor, setBackgroundImage]);

  useSceneSync({
    onScriptChange: handleScriptChange,
    onLayoutPresetChange: handleLayoutPresetChange,
    onBackgroundChange: handleBackgroundChange,
    onLayoutChange: handleLayoutChange,
    onSceneSwitch: handleSceneSwitch,
    skipBackgroundSync: isBlankModeFromUrl || showCountdown || recordingPending,
    skipLayoutSync: isBlankModeFromUrl,
    isRecording: isRecording,
  });

  // Sprint 19: Apply template aspect ratio when template is loaded
  useEffect(() => {
    if (loadedTemplate?.aspectRatio) {
      const templateRatio = loadedTemplate.aspectRatio;
      setCanvasRatio(templateRatio);
    }
  }, [loadedTemplate, setCanvasRatio]);

  // Sync project aspect ratio when project loads (preserves settings on refresh)
  // Note: aspectRatio is stored at project level, not scene level
  // FIXED: Don't override template aspect ratio if template is actively loaded
  // FIXED: Reset layout state when switching between projects with different aspect ratios
  const prevProjectIdRef = useRef<string | undefined>(undefined);
  // FIX: Track last applied dimensions to prevent duplicate preset applications
  const lastAppliedDimensionsRef = useRef<string>('');
  // FIX: Track quality to detect when dimensions change DUE TO quality change
  const prevQualityRef = useRef<string>(recordingQuality);
  // FIX: Flag to skip the NEXT dimension change when quality just changed
  const skipNextDimensionChangeRef = useRef<boolean>(false);
  // FIX: Track previous canvas dimensions for proportional scaling on quality change
  const prevCanvasDimensionsRef = useRef<{ width: number; height: number }>({ width: canvasWidth, height: canvasHeight });
  // FIX: Flag to prevent preset reapplication after quality-triggered scale
  const justScaledForQualityRef = useRef<boolean>(false);
  
  useEffect(() => {
    // Skip if a template with a specific aspect ratio is loaded - template takes precedence
    if (loadedTemplate?.aspectRatio) {
      return;
    }
    
    if (activeProject?.aspectRatio) {
      const isNewProject = prevProjectIdRef.current !== undefined && prevProjectIdRef.current !== activeProject.id;
      const isRatioChange = activeProject.aspectRatio !== canvasRatio;
      
      // If switching to a different project with a different aspect ratio, reset layout state
      // This forces the webcam/screen layers to be recreated with correct dimensions
      if (isNewProject && isRatioChange) {
        setCanvasInfos([]);
        setInitialStreamLayout([]);
        setLayoutVersion(prev => prev + 1);
        // Reset layout aspect ratio tracker to allow new layout to be applied
        layoutAspectRatioRef.current = activeProject.aspectRatio;
        // Also reset the dimension sync tracker so preset can be re-applied after dimensions update
        if (lastAppliedDimensionsRef.current) {
          lastAppliedDimensionsRef.current = '';
        }
      }
      
      setCanvasRatio(activeProject.aspectRatio);
      prevProjectIdRef.current = activeProject.id;
    }
  }, [activeProject?.aspectRatio, activeProject?.id, setCanvasRatio, loadedTemplate?.aspectRatio, setCanvasInfos, setInitialStreamLayout, setLayoutVersion, canvasRatio, canvasWidth, canvasHeight]);

  // FIX: Re-apply preset after canvas dimensions have updated to match project aspect ratio
  // This ensures the layout is computed with correct dimensions after aspect ratio change
  useEffect(() => {
    // Detect quality change FIRST before any other logic
    const qualityJustChanged = prevQualityRef.current !== recordingQuality;
    
    // If quality just changed, set flag to skip the next dimension update
    // CRITICAL: Save the CURRENT dimensions NOW before they change
    if (qualityJustChanged) {
      // Save current dimensions BEFORE they change due to quality
      prevCanvasDimensionsRef.current = { width: canvasWidth, height: canvasHeight };
      prevQualityRef.current = recordingQuality;
      skipNextDimensionChangeRef.current = true;
      return;
    }
    
    // Use project ratio if available, otherwise use current canvas ratio (for blank mode)
    const effectiveRatio = activeProject?.aspectRatio || canvasRatio;
    const ratioChanged = canvasRatio !== effectiveRatio;
    
    // Check if this is a dimension update following a quality change
    // Scale existing layout proportionally instead of re-applying preset
    // This works for BOTH project mode AND blank mode
    if (skipNextDimensionChangeRef.current && !ratioChanged) {
      skipNextDimensionChangeRef.current = false;
      
      // Get previous and new dimensions for scaling
      const prevWidth = prevCanvasDimensionsRef.current.width;
      const prevHeight = prevCanvasDimensionsRef.current.height;
      const newWidth = canvasWidth;
      const newHeight = canvasHeight;
      
      // Calculate scale factors
      const scaleX = newWidth / prevWidth;
      const scaleY = newHeight / prevHeight;
      
      // Always update dimension tracking on quality change, even if layout is empty
      // This prevents the dimension sync effect from re-applying the scene preset
      // when layout changes later (e.g., user toggles webcam after quality change)
      const dimensionKey = `${canvasWidth}x${canvasHeight}-${effectiveRatio}-${currentScene?.layoutPreset || 'blank'}`;
      lastAppliedDimensionsRef.current = dimensionKey;
      prevCanvasDimensionsRef.current = { width: newWidth, height: newHeight };

      // Scale the existing layout proportionally
      if (initialStreamLayout.length > 0) {
        const scaledLayout = initialStreamLayout.map(item => ({
          ...item,
          x: Math.round(item.x * scaleX),
          y: Math.round(item.y * scaleY),
          width: Math.round(item.width * scaleX),
          height: Math.round(item.height * scaleY),
        }));
        
        // Apply scaled layout
        setInitialStreamLayout(scaledLayout);
        setLayoutVersion(prev => prev + 1);
        
        // Set flag to prevent preset reapplication after quality-triggered scale
        justScaledForQualityRef.current = true;
        setTimeout(() => {
          justScaledForQualityRef.current = false;
        }, 500);
        
        // Request canvas re-render
        const canvas = stageRef?.current?.getCanvas?.();
        canvas?.requestRenderAll();
      }
      
      // Clear transition snapshot
      store.set(isRatioTransitioningAtom, false);
      store.set(canvasTransitionSnapshotAtom, null);
      
      return;
    }
    
    // Reset the skip flag if we get here (handles edge cases)
    skipNextDimensionChangeRef.current = false;
    
    // For the rest of the logic (preset application), require project mode
    // Blank mode doesn't need preset re-application on dimension sync
    if (!currentScene?.layoutPreset || !canvasReady) {
      return;
    }
    if (!activeProject?.aspectRatio) {
      return;
    }
    
    // Check if canvas dimensions match the project aspect ratio
    const projectRatio = activeProject.aspectRatio;
    const isPortrait = projectRatio === '9:16';
    const dimensionsCorrect = isPortrait 
      ? canvasWidth < canvasHeight 
      : canvasWidth >= canvasHeight;
    
    // Only apply if dimensions are correct AND we haven't already applied for these dimensions
    const dimensionKey = `${canvasWidth}x${canvasHeight}-${projectRatio}-${currentScene.layoutPreset}`;
    
    if (dimensionsCorrect && canvasRatio === projectRatio && dimensionKey !== lastAppliedDimensionsRef.current) {
      lastAppliedDimensionsRef.current = dimensionKey;
      // Update dimension tracking for future quality changes
      prevCanvasDimensionsRef.current = { width: canvasWidth, height: canvasHeight };
      
      // Re-trigger preset application now that dimensions are correct
      handleLayoutPresetChange(currentScene.layoutPreset, { fromSceneLoad: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvasWidth, canvasHeight, canvasRatio, canvasReady, currentScene?.layoutPreset, activeProject?.aspectRatio, handleLayoutPresetChange, recordingQuality]);

  // Add beforeunload warning for recording and upload states
  useBeforeUnloadWarning({
    isRecording,
    isPaused,
    activeUploads,
    uploadInitializing,
    pendingChunks,
  });

  //Streams Cleanup
  const webcamRef = useRef<MediaStream | null>(null);
  const micRef = useRef<MediaStream | null>(null);
  const screenRef = useRef<MediaStream | null>(null);
  const rawMicRef = useRef<MediaStream | null>(null);
  const stableLayoutRef = useRef(initialStreamLayout);
  const isRecordingRef = useRef(isRecording);

  const hasWebcam = useMemo(() => !!webcamStream, [webcamStream]);
  const hasScreen = useMemo(() => !!screenStream, [screenStream]);

  // Determine if retry button should be shown
  const shouldShowRetryButton = useMemo(() => {
    // Don't show retry button if no current video guid
    if (!currentVideoGuid) {
      return false;
    }

    // Show retry button when:
    // 1. Recording has stopped and there are upload errors
    // 2. There are failed chunks
    // 3. Upload initialization failed
    return (
      !isRecording &&
      !isUploading &&
      (uploadErrors.has(currentVideoGuid) ||
        failedChunks.has(currentVideoGuid) ||
        showRetryButton)
    );
  }, [
    isRecording,
    isUploading,
    currentVideoGuid,
    uploadErrors,
    failedChunks,
    showRetryButton,
  ]);

  useEffect(() => {
    setShowMutePopup(isMicrophoneMuted);
  }, [isMicrophoneMuted]);

  // Check browser compatibility on mount
  useEffect(() => {
    const warnings = getCompatibilityWarnings();
    const supported = isRecordingSupported();
    setCompatibilityWarnings(warnings);
    setRecordingSupported(supported);

    if (warnings.length > 0) {
      logger.warn("Browser compatibility warnings:", warnings);
    }
    if (!supported) {
      logger.error("Recording not supported in this browser");
    }
  }, []);

  // Keep them updated
  useEffect(() => {
    webcamRef.current = webcamStream;
  }, [webcamStream]);

  useEffect(() => {
    micRef.current = microphoneStream;
  }, [microphoneStream]);

  useEffect(() => {
    rawMicRef.current = rawMicrophoneStream;
  }, [rawMicrophoneStream]);

  useEffect(() => {
    screenRef.current = screenStream;
  }, [screenStream]);

  useEffect(() => {
    if (screenStream) {
      const track = screenStream.getVideoTracks()[0];
      const settings = track.getSettings();
      // Check if displaySurface property exists and is "monitor" for full screen share
      const isFullScreen =
        "displaySurface" in settings
          ? (settings as any).displaySurface === "monitor"
          : false;
      setIsFullScreenShare(isFullScreen);
    } else {
      setIsFullScreenShare(false);
    }
  }, [screenStream]);

  // Cleanup on unmount (backup for when component actually unmounts)
  // Main cleanup is handled by MediaStreamCleanup component at App level
  useEffect(() => {
    return () => {
      logger.info('🧹 CanvasRecorder unmounting - cleanup via refs');
      webcamRef.current?.getTracks().forEach((track) => track.stop());
      micRef.current?.getTracks().forEach((track) => track.stop());
      rawMicRef.current?.getTracks().forEach((track) => track.stop());
      screenRef.current?.getTracks().forEach((track) => track.stop());
    };
  }, []);

  // NOTE: Scene preset application is now handled by:
  // 1. useSceneSync - calls handleLayoutPresetChange when scene loads
  // 2. handleLayoutPresetChange - defers preset application if dimensions don't match
  // 3. DimensionSync effect - re-applies preset once dimensions are correct
  // The previous force-apply effect with setTimeout(100) caused race conditions
  // because the closure captured stale canvasWidth/canvasHeight values

  // Handle position changes from draggable streams
  const handlePositionChange = useCallback(
    (
      streamType: "screen" | "webcam",
      position: { x: number; y: number; width: number; height: number },
    ) => {
      setInitialStreamLayout((prev) => {
        const newLayout = prev.map((item) => {
          if (item.stream === streamType) {
            return {
              ...item,
              x: position.x,
              y: position.y,
              width: position.width,
              height: position.height,
            };
          }
          return item;
        });
        return newLayout;
      });
    },
    [setInitialStreamLayout],
  );
  // Removed unused initialCanvasSizeRef
  const previousWrapperSizeRef = useRef<{
    width: number;
    height: number;
  } | null>(null);

  // Calculate maximum available space for canvas considering toolbar
  const calculateCanvasDimensions = useCallback(() => {
    const container = canvasContainerRef.current;
    if (!container) {
      console.warn(
        "⚠️ [calculateCanvasDimensions] Container ref not available",
      );
      return { width: 0, height: 0 };
    }

    // Get the parent container
    const parentContainer = container.parentElement;
    if (!parentContainer) {
      console.warn(
        "⚠️ [calculateCanvasDimensions] Parent container not available",
      );
      return { width: 0, height: 0 };
    }

    // Force browser reflow to get fresh dimensions
    // This is needed because Chrome caches layout values when tab is "hidden" during screen sharing
    void parentContainer.offsetHeight;
    
    // Get the available space from the actual container
    const availableWidth = parentContainer.clientWidth;
    
    let availableHeight: number;

    // In slides mode (but NOT blank mode), use the actual container height (which changes when dragging divider)
    // Blank mode uses the simple canvas layout even though layoutMode may be 'slides'
    if (layoutMode === "slides" && !isBlankMode) {
      // Use container height minus padding-block (8px top + 8px bottom = 16px total)
      const CANVAS_PADDING = 16;
      // Force fresh read after reflow
      const containerHeight = parentContainer.clientHeight - CANVAS_PADDING;
      
      // Calculate max possible height based on viewport (header + toolbar + notes min + padding)
      // Notes panel minimum is ~100px, so max canvas area = innerHeight - 64 - 80 - 100 - 32
      const maxCanvasFromViewport = window.innerHeight - 64 - 80 - 100 - 32;
      
      // Use the smaller of container height or max possible from viewport
      // This ensures canvas shrinks when viewport shrinks (e.g., screen share bar)
      availableHeight = Math.min(containerHeight, maxCanvasFromViewport);
    } else {
      // Blank mode or Timeline mode - use window-based calculation
      // 64 = header, 80 = toolbar, 32 = padding
      availableHeight = window.innerHeight - 64 - 80 - 32;
    }

    let width = availableWidth;
    let height = availableHeight;

    // Calculate dimensions based on aspect ratio - always maximize to fill available space
    if (canvasRatio === "16:9") {
      // For 16:9, calculate the maximum size that fits within available space
      const aspectRatio = 16 / 9;

      // Calculate both possibilities
      const widthFromHeight = availableHeight * aspectRatio;
      const heightFromWidth = availableWidth / aspectRatio;

      // Choose the option that gives us the largest possible size
      if (widthFromHeight <= availableWidth) {
        // If we can fit the width calculated from height, use full height
        width = widthFromHeight;
        height = availableHeight;
      } else {
        // Otherwise, use full width and calculate height
        width = availableWidth;
        height = heightFromWidth;
      }
    } else if (canvasRatio === "1:1") {
      // For 1:1, use the smaller dimension
      const size = Math.min(availableWidth, availableHeight);
      width = size;
      height = size;
    } else if (canvasRatio === "9:16") {
      // For 9:16 (portrait), calculate both possibilities and choose the one that maximizes size
      const widthBasedOnHeight = (availableHeight * 9) / 16;
      const heightBasedOnWidth = (availableWidth * 16) / 9;

      if (widthBasedOnHeight <= availableWidth) {
        // If width based on height fits within available width, use that
        width = widthBasedOnHeight;
        height = availableHeight;
      } else {
        // Otherwise, use height based on available width
        width = availableWidth;
        height = heightBasedOnWidth;
      }
    }

    // Ensure dimensions are at least a minimum size
    const minSize = 200;
    width = Math.max(width, minSize);
    height = Math.max(height, minSize);

    // Round to even numbers to avoid subpixel rendering issues
    width = Math.floor(width / 2) * 2;
    height = Math.floor(height / 2) * 2;

    return { width, height };
  }, [canvasRatio, layoutMode, isBlankMode]);

  useLayoutEffect(() => {
    const handleResize = (source?: string) => {
      const dimensions = calculateCanvasDimensions();
      
      if (dimensions.width === 0 || dimensions.height === 0) {
        return;
      }

      const { width, height } = dimensions;

      if (
        previousWrapperSizeRef.current?.width === width &&
        previousWrapperSizeRef.current?.height === height
      ) {
        return;
      }
      setWrapperWidth(width);
      setWrapperHeight(height);

      previousWrapperSizeRef.current = { width, height };
      setCanvasReady(true);
    };

    // Initial resize with slight delay to ensure DOM is ready in slides mode
    const initialTimeout = setTimeout(() => handleResize('initial'), 0);

    // Also trigger after a short delay to catch late-rendering containers
    const delayedTimeout = setTimeout(() => handleResize('delayed'), 100);

    // Add resize event listener
    // In slides mode, no debouncing for immediate live resize during drag
    // In timeline mode, use debouncing to avoid excessive recalculations
    let resizeTimeout: NodeJS.Timeout;
    const resizeHandler = layoutMode === 'slides' 
      ? () => handleResize('window.resize') 
      : () => {
          clearTimeout(resizeTimeout);
          resizeTimeout = setTimeout(() => handleResize('window.resize-debounced'), 100);
        };

    window.addEventListener("resize", resizeHandler);

    // Add ResizeObserver to detect canvas container size changes
    // This catches browser chrome changes (like screen share banners) that don't trigger window.resize
    let resizeObserver: ResizeObserver | null = null;
    const container = canvasContainerRef.current;
    const parentContainer = container?.parentElement;
    
    if (container) {
      resizeObserver = new ResizeObserver((entries) => {
        const targets = entries.map(e => e.target === container ? 'container' : e.target === parentContainer ? 'parent' : 'body').join(',');
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => handleResize(`ResizeObserver(${targets})`), 50);
      });
      
      // Observe the canvas container
      resizeObserver.observe(container);
      
      // Also observe the parent container - this catches screen share banner changes
      // that push content down without triggering window.resize
      if (parentContainer) {
        resizeObserver.observe(parentContainer);
      }
      
      // Observe document.body for top-level layout changes (screen share banners, dev tools, etc.)
      resizeObserver.observe(document.body);
    }

    // Add visual viewport listener for Chrome's screen sharing bar
    // The visual viewport changes when browser UI elements (like screen share bar) appear
    const visualViewportHandler = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => handleResize('visualViewport'), 50);
    };
    
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', visualViewportHandler);
      window.visualViewport.addEventListener('scroll', visualViewportHandler);
    }

    // Visibility change handler - triggers when tab becomes active
    // This catches resize changes that happened while tab was hidden (e.g., screen share bar)
    const visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        handleResize('visibilitychange');
      }
    };
    document.addEventListener('visibilitychange', visibilityHandler);

    // Also trigger on focus (user clicks on the tab/window)
    const focusHandler = () => {
      handleResize('focus');
    };
    window.addEventListener('focus', focusHandler);

    // Cleanup
    return () => {
      window.removeEventListener("resize", resizeHandler);
      document.removeEventListener('visibilitychange', visibilityHandler);
      window.removeEventListener('focus', focusHandler);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', visualViewportHandler);
        window.visualViewport.removeEventListener('scroll', visualViewportHandler);
      }
      clearTimeout(resizeTimeout);
      clearTimeout(initialTimeout);
      clearTimeout(delayedTimeout);
    };
  }, [
    layoutMode,
    canvasRatio,
    setWrapperWidth,
    setWrapperHeight,
    setCanvasWidth,
    setCanvasHeight,
    setCanvasReady,
    calculateCanvasDimensions,
  ]);

  // Update canvas dimensions when recording quality changes

  useEffect(() => {
    const container = canvasContainerRef.current;
    if (!container) return;

    // Check if only recording quality changed - skip preset application to preserve UI positions
    const onlyRecordingQualityChanged =
      lastRecordingQualityRef.current !== recordingQuality;
    if (onlyRecordingQualityChanged) {
      lastRecordingQualityRef.current = recordingQuality;
      return;
    }

    // Don't apply presets automatically if user has manually modified the layout
    if (hasManualModifications) return;

    // This condition seems wrong - we should check if we need to apply the preset based on the preset itself
    // For now, let's just apply the preset when the selected preset changes
    if (!selectedPreset) return;

    const handler = setTimeout(() => {
      // @ts-ignore
      const presetFn = selectedPreset?.preset;

      const videoEl = screenVideoRef?.current || webcamVideoRef?.current;

      if (!presetFn || !videoEl?.videoWidth) return;

      const ratio = videoEl.videoWidth / videoEl.videoHeight;
      // Use resSize to get correct canvas dimensions based on current ratio and quality
      const { width: canvasWidth, height: canvasHeight } = resSize(recordingQuality, canvasRatio);
      applyPreset(
        presetFn,
        ratio,
        canvasWidth,
        canvasHeight,
        canvasRatio,
        undefined,
        true,
      ); // preserve manual modifications for auto-apply
    }, 0);

    return () => clearTimeout(handler);
  }, [
    applyPreset,
    canvasRatio,
    selectedPreset,
    screenVideoRef,
    webcamVideoRef,
    hasManualModifications,
    recordingQuality,
  ]);

  // Use a ref to track the current manual modifications state to avoid stale closures
  const hasManualModificationsRef = useRef(hasManualModifications);
  const lastRecordingQualityRef = useRef(recordingQuality);
  useEffect(() => {
    hasManualModificationsRef.current = hasManualModifications;
  }, [hasManualModifications]);

  // Auto-apply default layout when streams are available but no layout is set
  useEffect(() => {
    if (
      !canvasReady ||
      initialStreamLayout.length > 0 ||
      hasManualModificationsRef.current
    ) {
      if (hasManualModificationsRef.current) {
        logger.info(
          "Skipping auto preset - layout has manual modifications (ref)",
        );
      }
      return;
    }

    // Skip auto-preset if current scene has a defined preset - preserve scene settings
    if (currentScene?.layoutPreset) {
      logger.info(
        "Skipping auto preset - scene has defined preset:",
        currentScene.layoutPreset,
      );
      return;
    }

    // Skip auto-preset when both streams are available - let useAutoLayoutPreset handle it
    if (hasWebcam && hasScreen) {
      logger.info(
        "Skipping auto preset - both streams available, useAutoLayoutPreset will handle it",
      );
      return;
    }

    if (hasWebcam || hasScreen) {
      // Debug logging disabled to reduce console bloat

      const videoElement = hasScreen
        ? screenVideoRef.current
        : webcamVideoRef.current;

      if (videoElement && videoElement.videoWidth > 0) {
        const screenAspectRatio =
          videoElement.videoWidth / videoElement.videoHeight;

        // Debug logging disabled to reduce console bloat

        // Import the preset function and apply it
        // Use resSize to get correct canvas dimensions based on current ratio and quality
        const { width: canvasWidth, height: canvasHeight } = resSize(recordingQuality, canvasRatio);
        if (hasWebcam && !hasScreen) {
          // Only webcam available - use webcam full preset
          import("./presets/layoutPresets").then(({ Preset_WebcamFull }) => {
            logger.info(
              "Auto-applying WebcamFull preset - checking manual modifications (ref):",
              hasManualModificationsRef.current,
            );
            if (hasManualModificationsRef.current) {
              logger.info(
                "Skipping WebcamFull preset application - layout has manual modifications (ref)",
              );
              return;
            }
            logger.info(
              "🔵 Auto-applying WebcamFull preset (from auto-detect)",
            );
            applyPreset(
              Preset_WebcamFull,
              screenAspectRatio,
              canvasWidth,
              canvasHeight,
              canvasRatio,
              roundedWebcam,
              true, // preserve manual modifications for auto-apply
            );
          });
        } else if (hasScreen && !hasWebcam) {
          // Only screen available - use screen full preset
          import("./presets/layoutPresets").then(({ Preset_ScreenFull }) => {
            logger.info(
              "Auto-applying ScreenFull preset - checking manual modifications (ref):",
              hasManualModificationsRef.current,
            );
            if (hasManualModificationsRef.current) {
              logger.info(
                "Skipping ScreenFull preset application - layout has manual modifications (ref)",
              );
              return;
            }
            applyPreset(
              Preset_ScreenFull,
              screenAspectRatio,
              canvasWidth,
              canvasHeight,
              canvasRatio,
              undefined,
              true, // preserve manual modifications for auto-apply
            );
          });
        }
      }
    }
  }, [
    canvasReady,
    initialStreamLayout.length,
    canvasRatio,
    recordingQuality,
    applyPreset,
    screenVideoRef,
    webcamVideoRef,
    roundedWebcam,
    hasManualModifications,
    hasWebcam,
    hasScreen,
    currentScene?.layoutPreset,
  ]);

  // Debug logging for stream visibility - disabled to reduce console bloat

  const stableLayout = useMemo(
    () => initialStreamLayout,
    [initialStreamLayout],
  );

  // ────────────────────────────────────────────────────────────
  // 1) Stable selection callbacks (no new function each render)
  // ────────────────────────────────────────────────────────────
  // Selection callbacks removed as they were unused
  // Update ref without causing re-renders
  useEffect(() => {
    stableLayoutRef.current = initialStreamLayout;
    isRecordingRef.current = isRecording;
  }, [initialStreamLayout, isRecording, layoutVersion]);

  // Use useCallback for stable function reference
  const getCurrentLayout = useCallback(() => {
    return {
      stableLayout: stableLayoutRef.current,
      isRecording: isRecordingRef.current,
    };
  }, []);

  const streamElements = useMemo(() => {
    if (!canvasReady) return null;
    return stableLayout.map((layout) => {
      const stream = layout.stream === "screen" ? screenStream : webcamStream;
      const videoElement =
        layout.stream === "screen"
          ? screenVideoRef.current
          : webcamVideoRef.current;

      if (!stream || !videoElement) {
        return null;
      }

      const isSelected = selectedStream === layout.stream;

      // Use a stable key based on stream type and layout version to balance re-renders
      // This prevents unnecessary re-renders during resize/moving but allows updates when layout actually changes
      const stableKey = `${layout.stream}-${layoutVersion}`;
      
      return (
        <DraggableResizableStream
          key={stableKey}
          videoElement={videoElement}
          initialX={layout.x}
          initialY={layout.y}
          initialWidth={layout.width}
          initialHeight={layout.height}
          streamType={layout.stream}
          isSelected={isSelected}
          onPositionChange={handlePositionChange}
          isManualPreset={isManualPreset}
          getCurrentLayout={getCurrentLayout}
        />
      );
    });
  }, [
    canvasReady,
    stableLayout,
    screenStream,
    webcamStream,
    screenVideoRef,
    webcamVideoRef,
    selectedStream,
    handlePositionChange,
    isManualPreset,
    getCurrentLayout,
    layoutVersion,
  ]);

  // Phase 3: Browser detection check (must happen before mobile check for proper prioritization)
  if (isBrowserDetectionEnabled && isUnsupportedBrowser && unsupportedBrowserType && !browserWarningDismissed) {
    // Firefox gets hard block, Safari gets dismissable warning
    if (unsupportedBrowserType === 'firefox' || !browserWarningDismissed) {
      return (
        <BrowserNotAllowedOverlay
          visible={true}
          browserType={unsupportedBrowserType}
          onDismiss={() => setBrowserWarningDismissed(true)}
        />
      );
    }
  }

  // Show mobile not allowed overlay if on mobile device or mobile viewport
  if (isMobile || isMobileViewport) {
    return (
      <MobileNotAllowedOverlay
        deviceType={deviceType}
        isMobileViewport={isMobileViewport}
      />
    );
  }

  // Show browser compatibility warnings
  if (compatibilityWarnings.length > 0) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          padding: "20px",
        }}
      >
        <Alert
          message="Browser Compatibility Issues"
          description={
            <div>
              <p>The following compatibility issues were detected:</p>
              <ul>
                {compatibilityWarnings.map((warning, index) => (
                  <li key={index}>{warning}</li>
                ))}
              </ul>
              <p>
                Recording may not work properly in this browser. Please try
                using Chrome, Firefox, or Edge for the best experience.
              </p>
            </div>
          }
          type="warning"
          showIcon
          style={{ maxWidth: "600px" }}
        />
      </div>
    );
  }

  // Show recording not supported message
  if (!recordingSupported) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          padding: "20px",
        }}
      >
        <Alert
          message="Recording Not Supported"
          description={
            <div>
              <p>
                Recording is not supported in this browser. This may be due to:
              </p>
              <ul>
                <li>Outdated browser version</li>
                <li>Missing MediaRecorder API support</li>
                <li>Security restrictions</li>
              </ul>
              <p>
                Please try using a modern browser like Chrome, Firefox, or Edge.
              </p>
            </div>
          }
          type="error"
          showIcon
          style={{ maxWidth: "600px" }}
        />
      </div>
    );
  }

  // Show loading spinner while project data is being fetched
  if (isProjectLoading) {
    return (
      <div
        style={{
          position: "fixed",
          top: 64,
          left: 0,
          right: 0,
          height: "calc(100vh - 64px)",
          background: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  // Render Slides Layout (starts below header) - only for scene-based projects, not blank/quick mode
  if (layoutMode === "slides" && !isBlankMode) {
    return (
      <div
        style={{
          position: "fixed",
          top: 64, // Start below the 64px header
          left: 0,
          right: 0,
          height: "calc(100vh - 64px)", // Fill remaining viewport height
          background: "#fff",
          overflow: "hidden",
        }}
      >
        {/* Slides Layout Component */}
        <SlidesLayout
          canvasComponent={
            <div
              id="recording-area"
              ref={(el) => {
                // Attach both refs - canvasContainerRef for sizing, recordingAreaRef for outside click detection
                if (canvasContainerRef) (canvasContainerRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
                (recordingAreaRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
              }}
              style={{
                width: wrapperWidth,
                height: wrapperHeight,
                borderRadius: 10,
                position: "relative",
                transition: "none",
              }}
            >
              <CanvasStage />
              <CanvasBackground />
              {streamElements}
              {selectedStream && stageRef?.current && (
                <SelectionToolbar
                  canvas={stageRef.current.getCanvas()}
                  isRecordingRef={isRecordingRef}
                  webcamObjectId="webcam-frame"
                />
              )}

              {/* NoStreamLayer overlay for slides mode - shows when no streams active */}
              {/* Hidden for presentation-imported scenes which only need the slide background */}
              {!hasAnyCanvasStream && canvasReady && !hasWebcam && !hasScreen && (currentScene as any)?.layoutPreset !== 'Preset_PresentationSlide' && (
                <NoStreamLayer
                  ratio={canvasRatio}
                  canvasWidth={canvasWidth}
                  canvasHeight={canvasHeight}
                  wrapperWidth={wrapperWidth}
                  wrapperHeight={wrapperHeight}
                />
              )}
              
              {/* Countdown overlay for slides mode */}
              {showCountdown && (
                <Countdown
                  onComplete={() => {
                    setShowCountdown(false);
                  }}
                  showCountdown={showCountdown}
                />
              )}
            </div>
          }
          recordingControls={<RecordingToolbarBottom ref={recordingToolbarRef} layerRef={layerRef} />}
          canvasRatio={canvasRatio}
          liveBackgroundColor={backgroundColor}
          liveBackgroundImage={backgroundImage}
          isUpdatingScene={isUpdatingScene}
          savingTransitionSceneId={savingTransitionSceneId}
          onProjectTitleChange={handleProjectTitleChange}
          onAddScene={handleAddScene}
          onRenameScene={handleRenameScene}
          onDuplicateScene={handleDuplicateSpecificScene}
          onDeleteScene={handleDeleteScene}
          onReorderScenes={handleReorderScenes}
          onSceneTitleChange={handleSceneTitleChange}
          onSceneTagsChange={handleSceneTagsChange}
          onSceneNotesChange={handleSceneNotesChange}
          onCopySettings={handleCopySettings}
          onJumpToRecording={handleJumpToRecording}
          onRetakeScene={handleRetakeScene}
          onEditSettings={handleEditSettings}
          onExportScene={handleExportScene}
          onResetVideo={handleResetVideo}
          onPreviewScene={handlePreviewScene}
          onConfirmScene={handleConfirmScene}
          onEditTransition={(fromSceneIndex) => handleOpenTransitionStudio(fromSceneIndex)}
          onPreviewTransition={(fromSceneIndex) => handleOpenTransitionStudio(fromSceneIndex)}
          onResizeComplete={() => {
            // Trigger canvas recalculation and quality upgrade
            logger.info('[CanvasRecorder] Resize complete, triggering canvas quality upgrade');
            window.dispatchEvent(new Event('resize'));
          }}
          onResetBackground={handleResetBackground}
        />

        {/* Modals (shared between both layouts) */}
        <CopySettingsModal
          visible={copySettingsModalVisible}
          sourceScene={copySettingsSourceScene}
          targetScenes={sceneList.filter(
            (s) => s.id !== copySettingsSourceScene?.id,
          )}
          onConfirm={handleCopySettingsConfirm}
          onCancel={() => setCopySettingsModalVisible(false)}
        />

        <EditSettingsModal
          visible={editSettingsModalVisible}
          scene={editSettingsScene}
          onConfirm={handleEditSettingsConfirm}
          onCancel={() => setEditSettingsModalVisible(false)}
        />

        <ScenePreviewModal
          key={`scene-preview-${scenePreviewScene?.id}-${scenePreviewScene?.videoGuid || 'no-video'}`}
          visible={scenePreviewModalVisible}
          scene={scenePreviewScene}
          projectId={activeProject?.id}
          onClose={() => setScenePreviewModalVisible(false)}
          onRetake={handleScenePreviewRetake}
          onDownload={handleScenePreviewDownload}
        />

        <RecordingPreviewModal
          onDelete={deleteRecording}
          onReRecord={(sceneId) => {
            logger.info(
              "[CanvasRecorder] Re-record requested for scene:",
              sceneId,
            );
            handleRetakeScene(sceneId);
          }}
          recoveredVideoUrl={recoveredVideoUrl}
          recoveredPendingData={recoveredPendingData}
          onClearRecoveredData={() => {
            // Revoke blob URL to prevent memory leaks
            if (recoveredVideoUrl) {
              URL.revokeObjectURL(recoveredVideoUrl);
            }
            setRecoveredVideoUrl(null);
            setRecoveredPendingData(null);
          }}
        />

        <SceneStitchingEngine
          visible={isStitchingModalVisible}
          onClose={() => setIsStitchingModalVisible(false)}
          onComplete={(videoBlob) => {
            logger.info(
              "[CanvasRecorder] Stitching complete, video blob size:",
              videoBlob.size,
            );
          }}
        />

        <AddSceneModal
          visible={isAddSceneModalVisible}
          onClose={() => setIsAddSceneModalVisible(false)}
          onCreateBlank={handleCreateBlankScene}
          onDuplicate={handleDuplicateScene}
          hasPreviousScene={sceneList.findIndex(s => s.id === selectedSceneId) > 0}
        />

        {/* Delete Scene Confirmation Modal */}
        <Modal
          title="Delete Scene"
          open={isDeleteSceneModalVisible}
          onOk={confirmDeleteScene}
          onCancel={() => {
            if (isDeletingScene) return;
            setIsDeleteSceneModalVisible(false);
            setSceneToDelete(null);
          }}
          okText={isDeletingScene ? "Deleting..." : "Delete"}
          cancelText="Cancel"
          okButtonProps={{ danger: true, loading: isDeletingScene }}
          cancelButtonProps={{ disabled: isDeletingScene }}
          closable={!isDeletingScene}
          maskClosable={!isDeletingScene}
          centered
        >
          <p>Are you sure you want to delete this scene?</p>
          <p><strong>{sceneList.find((s) => s.id === sceneToDelete)?.title}</strong></p>
          <p style={{ color: '#ff4d4f', marginTop: '12px' }}>
            This action cannot be undone. All recordings and settings will be permanently deleted.
          </p>
        </Modal>

        {/* Unified Transition Studio Modal */}
        {transitionStudioSceneId && (() => {
          const studioScene = sceneList.find(s => s.id === transitionStudioSceneId);
          const studioIndex = sceneList.findIndex(s => s.id === transitionStudioSceneId);
          const nextScene = studioIndex + 1 < sceneList.length ? sceneList[studioIndex + 1] : undefined;
          const beforeSceneDuration = studioScene?.duration;
          const afterSceneDuration = nextScene?.duration;
          
          return studioScene && nextScene && (
            <TransitionStudioModal
              visible={true}
              transition={studioScene.transitionOut || getDefaultTransition()}
              fromScene={studioScene}
              toScene={nextScene}
              projectId={activeProject?.id}
              onClose={() => setTransitionStudioSceneId(null)}
              onChange={(transition) => handleTransitionChange(transitionStudioSceneId, transition)}
              onApplyToAll={handleApplyTransitionToAll}
              onApplyToRemaining={(transition) => handleApplyTransitionToRemaining(transitionStudioSceneId, transition)}
              isFirstTransition={studioIndex === 0}
              isLastTransition={studioIndex === sceneList.length - 1}
              beforeSceneDuration={beforeSceneDuration}
              afterSceneDuration={afterSceneDuration}
              disabled={isRecording}
            />
          );
        })()}
      </div>
    );
  }

  // Render Timeline Layout (default)
  return (
    <div
      ref={canvasContainerRef}
      style={{
        marginInline: "auto",
        height: "100%",
        width: "100%",
        maxWidth: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div className="d-flex justify-content-center" style={{ width: "100%", maxWidth: "100%" }}>
        <Flex vertical gap={16} align="center" style={{ width: "100%", maxWidth: "100%", minWidth: 0 }}>
          {!isRecording && !showCountdown && (
            <MutePopupOverlay
              onClose={() => setShowMutePopup(false)}
              showPopup={showMutePopup}
            />
          )}
          <div
            id="recording-area"
            style={{
              width: wrapperWidth,
              height: wrapperHeight,
              borderRadius: 10,
              position: "relative",
              transition: "none",
            }}
            ref={recordingAreaRef}
          >
            <CanvasStage />
            <CanvasBackground />

            {/* Streams */}
            {streamElements}

            {/* Full Screen Overlay - positioned over screen share layer only */}
            {isFullScreenShare && screenStream && (
              <>{/* <FullScreenOverlay ratio={canvasRatio} /> */}</>
            )}

            {/* Selection Toolbar */}
            {stageRef?.current && (
              <SelectionToolbar
                canvas={stageRef.current.getCanvas()}
                isRecordingRef={isRecordingRef}
                webcamObjectId="webcam-frame"
              />
            )}

            {!hasAnyCanvasStream && canvasReady && !hasWebcam && !hasScreen && (currentScene as any)?.layoutPreset !== 'Preset_PresentationSlide' && (
              <NoStreamLayer
                ratio={canvasRatio}
                canvasWidth={canvasWidth}
                canvasHeight={canvasHeight}
                wrapperWidth={wrapperWidth}
                wrapperHeight={wrapperHeight}
              />
            )}

            {showCountdown && (
              <Countdown
                onComplete={() => {
                  setShowCountdown(false);
                }}
                showCountdown={showCountdown}
              />
            )}

            {isBlankModeFromUrl && !isRecording && !isUploading && recoverableSessions.length > 0 && (
              <BlankRecordingRecoveryBanner
                sessions={recoverableSessions}
                isRecovering={isRecoveringBlankSession}
                recoveryProgress={blankRecoveryProgress}
                onRecover={recoverBlankSession}
                onDiscard={discardBlankSession}
                onDiscardAll={discardAllBlankSessions}
              />
            )}

            {/* Saving video overlay */}
            {isUploading &&
              !isRecording &&
              !shouldShowRetryButton &&
              !showStuckChunksOverlay &&
              !isDeleting &&
              !showDownloadOnlyOverlay &&
              (isBlankModeFromUrl || !recordingPreview.visible) && <SavingVideoOverlay />}

            {/* Stuck chunks overlay - only for single recordings, not scene-based projects */}
            {(showStuckChunksOverlay || shouldShowRetryButton) &&
              !isUploading &&
              !isRecording &&
              !isDeleting &&
              !showDownloadOnlyOverlay &&
              // Only show for single-recording mode: blank mode OR project with no scenes (after it loads)
              // Don't show during project load to prevent flash
              (isBlankMode || (activeProject !== null && sceneList.length === 0)) && (
                <StuckChunksOverlay
                  stuckChunks={stuckChunks}
                  totalStuckChunks={totalStuckChunks}
                  isProcessingStuckChunks={isProcessingStuckChunks}
                  isDownloadingAndDiscarding={isDownloadingAndDiscarding}
                  downloadProgress={downloadProgress}
                  onDiscardStuckChunks={onDiscardStuckChunks}
                  onRetryStuckChunks={onRetryStuckChunks}
                  handleDownloadAndDiscard={handleDownloadAndDiscard}
                />
              )}

            {/* Download-only overlay for unauthenticated users */}
            {showDownloadOnlyOverlay && !isRecording && (
              <DownloadOnlyOverlay
                handleDownloadAndDiscard={handleDownloadAndDiscard}
                onDiscardStuckChunks={() => {
                  onDiscardStuckChunks();
                }}
                stuckChunks={stuckChunks}
                totalStuckChunks={totalStuckChunks}
                isDownloadingAndDiscarding={isDownloadingAndDiscarding}
                downloadProgress={downloadProgress}
              />
            )}
          </div>

          <div className="recording-controls-container" style={{ width: '100%' }}>
            <RecordingToolbarBottom ref={recordingToolbarRef} layerRef={layerRef} />
          </div>
        </Flex>
      </div>

      {/* Render only the latest upload completion notification */}
      {(() => {
        const latestNotification = getLatestNotification();
        return latestNotification ? (
          <UploadCompleteNotification
            key={latestNotification.videoId}
            videoId={latestNotification.videoId}
            videoGuid={latestNotification.videoGuid}
            onClose={() => handleNotificationClose(latestNotification.videoId)}
          />
        ) : null;
      })()}

      {/* Sprint 10: Real-time upload monitoring components */}
      {uploads.length > 0 && (
        <LiveUploadMonitor
          uploads={uploads}
          onPause={pauseUpload}
          onResume={resumeUpload}
          onCancel={cancelUpload}
        />
      )}

      {/* Sprint 10: Network Quality Widget - disabled, telemetry is console-only */}
      {/* Network quality monitoring runs in background, no UI display */}


      {/* Sprint 14: Template Gallery Modal - Hide when using dashboard entry points */}
      {false && isTemplatesUIEnabled && !isRecording && (
        <>
          <Tooltip title="Start with a blank canvas and build your own custom multi-scene project">
            <Button
              size="large"
              icon={<PlusCircle size={18} />}
              onClick={handleStartBlankProject}
              style={{
                position: "fixed",
                top: "80px",
                right: isAIEnabled ? "390px" : "220px",
                zIndex: 1000,
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              }}
            >
              Start Blank Project
            </Button>
          </Tooltip>

          <Tooltip title="Browse 12 professional templates for education, business, content creation, and more">
            <Button
              type="primary"
              size="large"
              icon={<FileText size={18} />}
              onClick={() => setShowTemplateGallery(true)}
              style={{
                position: "fixed",
                top: "80px",
                right: "24px",
                zIndex: 1000,
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              }}
            >
              Browse Templates
            </Button>
          </Tooltip>

          <Modal
            title={null}
            open={showTemplateGallery && !hideTemplateGalleryModal}
            onCancel={() => setShowTemplateGallery(false)}
            footer={null}
            width="90%"
            style={{ maxWidth: "1400px", top: 20 }}
            bodyStyle={{ padding: "24px", maxHeight: "80vh", overflow: "auto" }}
            destroyOnHidden={false}
            className="template-gallery-modal"
          >
            <TemplateGallery
              onTemplateSelect={() => setShowTemplateGallery(false)}
              onParentModalVisibility={(visible) => {
                setHideTemplateGalleryModal(!visible);
              }}
            />
          </Modal>
        </>
      )}

      {/* Sprint 18: Platform Export Modals */}
      {isPlatformExportEnabled && (
        <>
          <PlatformExportSelector videoBlob={new Blob()} onClose={() => {}} />
          <ExportProgressModal />
        </>
      )}

      {/* Unified Recording Preview Modal */}
      <RecordingPreviewModal
        onDelete={deleteRecording}
        onReRecord={(sceneId) => {
          logger.info(
            "[CanvasRecorder] Re-record requested for scene:",
            sceneId,
          );
          handleRetakeScene(sceneId);
        }}
        recoveredVideoUrl={recoveredVideoUrl}
        recoveredPendingData={recoveredPendingData}
        onClearRecoveredData={() => {
          // Revoke blob URL to prevent memory leaks
          if (recoveredVideoUrl) {
            URL.revokeObjectURL(recoveredVideoUrl);
          }
          setRecoveredVideoUrl(null);
          setRecoveredPendingData(null);
        }}
      />

      {/* Slides Layout Modals */}
      <CopySettingsModal
        visible={copySettingsModalVisible}
        sourceScene={copySettingsSourceScene}
        targetScenes={sceneList.filter(
          (s) => s.id !== copySettingsSourceScene?.id,
        )}
        onConfirm={handleCopySettingsConfirm}
        onCancel={() => setCopySettingsModalVisible(false)}
      />

      <EditSettingsModal
        visible={editSettingsModalVisible}
        scene={editSettingsScene}
        onConfirm={handleEditSettingsConfirm}
        onCancel={() => setEditSettingsModalVisible(false)}
      />

      <ScenePreviewModal
        key={`scene-preview-${scenePreviewScene?.id}-${scenePreviewScene?.videoGuid || 'no-video'}`}
        visible={scenePreviewModalVisible}
        scene={scenePreviewScene}
        projectId={activeProject?.id}
        onClose={() => setScenePreviewModalVisible(false)}
        onRetake={handleScenePreviewRetake}
        onDownload={handleScenePreviewDownload}
      />

      {/* Scene Stitching Engine Modal */}
      <SceneStitchingEngine
        visible={isStitchingModalVisible}
        onClose={() => setIsStitchingModalVisible(false)}
        onComplete={(videoBlob) => {
          logger.info(
            "[CanvasRecorder] Stitching complete, video blob size:",
            videoBlob.size,
          );
          // Don't auto-close - let user see success state and download/export buttons
          // TODO: Open platform export selector with stitched blob
        }}
      />

      {/* Add Scene Modal */}
      <AddSceneModal
        visible={isAddSceneModalVisible}
        onClose={() => setIsAddSceneModalVisible(false)}
        onCreateBlank={handleCreateBlankScene}
        onDuplicate={handleDuplicateScene}
        hasPreviousScene={sceneList.findIndex(s => s.id === selectedSceneId) > 0}
      />

      {/* Sprint 17: AI Delivery Coaching Overlay */}
      {isDeliveryCoachingEnabled && (
        <CoachingOverlay isRecording={isRecording} isPaused={isPaused} isMicEnabled={!isMicrophoneMuted} position="bottom-right" />
      )}

      {/* Scene creation/duplication overlay */}
      <ProjectCreationOverlay visible={isCreatingScene} message={sceneCreationMessage} />
    </div>
  );
};

export default CanvasRecorder;
